import {
  startTransition,
  useEffect,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  apiClient,
  type B2cStockBatchRecord,
  type OperatorCardRecord,
  type OperatorOrganizationDetail,
  type OperatorOrganizationRecord,
  type PlantationPolicyRecord,
  type PlantationSirenRecord,
  type PublicLookupData,
} from "@/lib/api/client"
import {
  PLANTATION_LOCAL_LANGUAGE_OPTIONS,
  getPlantationLocalLanguageOption,
} from "@/lib/safetyqr-reporting-templates"
import {
  OperatorEmptyState,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  canAccessAdminWorkspace,
  canManagePortalOrganizations,
  resolveOperatorSession,
  titleizeToken,
  type OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"

const INPUT_CLASS =
  "w-full rounded-2xl border border-border/70 bg-background/85 px-3 py-2 text-sm shadow-xs outline-none transition-colors focus:border-primary/50"

const SECTION_OPTIONS = [
  { label: "Overview", value: "overview" },
  { label: "Cards", value: "cards" },
  { label: "Organizations", value: "organizations" },
] as const

type AdminSection = (typeof SECTION_OPTIONS)[number]["value"]

export interface AdminDashboardSearch {
  organizationId?: string
  section?: AdminSection
}

interface AdminWorkspaceProps {
  onSearchChange: (next: Partial<AdminDashboardSearch>) => void
  search: AdminDashboardSearch
}

interface AdminBundle {
  cards: OperatorCardRecord[]
  fleetRegistrations: PublicLookupData[]
  organizations: OperatorOrganizationRecord[]
  scopedOrganization: OperatorOrganizationDetail | null
  session: OperatorResolvedSession
  stockBatches: B2cStockBatchRecord[]
}

interface CreateOrganizationFormState {
  approximateBlockCount: string
  approximateRowCount: string
  approximateTreeCount: string
  billingEmail: string
  city: string
  contactEmail: string
  contactPhone: string
  customerCategory: string
  defaultYieldUnit: string
  fleetCategory: string
  fleetEnabled: boolean
  fleetSize: string
  goLiveTargetDate: string
  implementationMode: string
  legalName: string
  name: string
  notificationEmail: string
  notificationPhone: string
  notificationWhatsapp: string
  onboardingOwnerInternal: string
  operatingDistrict: string
  operatingPostalCode: string
  operatingState: string
  operatingTaluk: string
  operatingVillage: string
  orchardCount: string
  pilotStartedAt: string
  pilotStatus: string
  primaryContactName: string
  primaryCropType: string
  primaryOwnerDesignation: string
  primaryOwnerEmail: string
  primaryOwnerName: string
  primaryOwnerPhone: string
  primaryOwnerPreferredLanguage: string
  primaryVariety: string
  qrRolloutRequired: boolean
  region: string
  treeImportMode: string
  type: string
}

interface NotificationChannelFormState {
  notificationEmail: string
  notificationPhone: string
  notificationWhatsapp: string
}

interface PlantationPolicyFormState {
  defaultLocalLanguageCode: string
  dualLanguageEnabled: boolean
  notifyEmergencyContactsOnPanic: boolean
  notifyEmergencyContactsOnReport: boolean
  notifyNearestAuthority: boolean
  panicSmsEnabled: boolean
  panicSmsRecipientLimit: string
  plantationName: string
  sirenCooldownMinutes: string
  sirenFailureDisableThreshold: string
  sirenHeartbeatStaleMinutes: string
  sirenRoutingMode:
    | ""
    | "all_site_sirens"
    | "zone_only"
    | "zone_plus_central"
    | "healthy_zone_then_central"
    | "ordered_health_escalation"
    | "zone_then_nearest_healthy"
    | "nearest_healthy_then_central"
  triggerSirensOnPanic: boolean
}

interface PlantationDirectoryEntry {
  id: string
  plantationName: string
  preferredLanguageCode: string | null
  tagCount: number
  zoneCount: number
  zones: string[]
}

interface PlantationSirenFormState {
  displayName: string
  escalationOrder: string
  latitude: string
  locationLabel: string
  longitude: string
  phoneNumber: string
  triggerOnPanic: boolean
  zoneName: string
}

interface PlantationSirenInstallerPacket {
  apiKey: string
  displayName: string
  endpointPath: string
  generatedAt: string
  headerName: string
  preview: string
  sirenId: string
}

declare global {
  interface Window {
    QRCode?: QRCodeLibrary
  }
}

type QRCodeInstance = {
  clear?: () => void
}

type QRCodeConstructor = new (
  element: HTMLElement,
  options: {
    colorDark?: string
    colorLight?: string
    correctLevel?: unknown
    height?: number
    text: string
    width?: number
  }
) => QRCodeInstance

type QRCodeLibrary = QRCodeConstructor & {
  CorrectLevel?: {
    M?: unknown
  }
}

let qrCodeLibraryPromise: Promise<void> | null = null

const INITIAL_CREATE_FORM: CreateOrganizationFormState = {
  approximateBlockCount: "",
  approximateRowCount: "",
  approximateTreeCount: "",
  billingEmail: "",
  city: "",
  contactEmail: "",
  contactPhone: "",
  customerCategory: "",
  defaultYieldUnit: "kg",
  fleetCategory: "",
  fleetEnabled: false,
  fleetSize: "",
  goLiveTargetDate: "",
  implementationMode: "growbetter_assisted",
  legalName: "",
  name: "",
  notificationEmail: "",
  notificationPhone: "",
  notificationWhatsapp: "",
  onboardingOwnerInternal: "",
  operatingDistrict: "",
  operatingPostalCode: "",
  operatingState: "",
  operatingTaluk: "",
  operatingVillage: "",
  orchardCount: "1",
  pilotStartedAt: "",
  pilotStatus: "",
  primaryContactName: "",
  primaryCropType: "",
  primaryOwnerDesignation: "",
  primaryOwnerEmail: "",
  primaryOwnerName: "",
  primaryOwnerPhone: "",
  primaryOwnerPreferredLanguage: "en",
  primaryVariety: "",
  qrRolloutRequired: true,
  region: "",
  treeImportMode: "assisted_csv",
  type: "traffic_police",
}

const INITIAL_NOTIFICATION_CHANNEL_FORM: NotificationChannelFormState = {
  notificationEmail: "",
  notificationPhone: "",
  notificationWhatsapp: "",
}

const INITIAL_PLANTATION_POLICY_FORM: PlantationPolicyFormState = {
  defaultLocalLanguageCode: "",
  dualLanguageEnabled: false,
  notifyEmergencyContactsOnPanic: true,
  notifyEmergencyContactsOnReport: true,
  notifyNearestAuthority: false,
  panicSmsEnabled: true,
  panicSmsRecipientLimit: "2",
  plantationName: "",
  sirenCooldownMinutes: "5",
  sirenFailureDisableThreshold: "3",
  sirenHeartbeatStaleMinutes: "180",
  sirenRoutingMode: "",
  triggerSirensOnPanic: false,
}

const PLANTATION_SIREN_ROUTING_OPTIONS = [
  { label: "Select siren routing", value: "" },
  { label: "Zone only", value: "zone_only" },
  { label: "Zone + central", value: "zone_plus_central" },
  { label: "All site sirens", value: "all_site_sirens" },
  { label: "Healthy zone then central", value: "healthy_zone_then_central" },
  { label: "Best healthy siren by order", value: "ordered_health_escalation" },
  { label: "Zone then nearest healthy", value: "zone_then_nearest_healthy" },
  { label: "Nearest healthy then central", value: "nearest_healthy_then_central" },
] as const

const INITIAL_PLANTATION_SIREN_FORM: PlantationSirenFormState = {
  displayName: "",
  escalationOrder: "0",
  latitude: "",
  locationLabel: "",
  longitude: "",
  phoneNumber: "",
  triggerOnPanic: true,
  zoneName: "",
}

function normalizeToken(value: string | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatDateTimeLocalInputValue(value: string | null | undefined) {
  if (!value) {
    return ""
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ""
  }

  const offsetMs = parsed.getTimezoneOffset() * 60_000
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16)
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;")
}

function loadQrCodeLibrary() {
  if (typeof window === "undefined") {
    return Promise.resolve()
  }

  if (typeof window.QRCode === "function") {
    return Promise.resolve()
  }

  if (qrCodeLibraryPromise) {
    return qrCodeLibraryPromise
  }

  qrCodeLibraryPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script")
    script.src = "/admin/vendor/qrcode.min.js"
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Unable to load QRCode renderer"))
    document.head.appendChild(script)
  })

  return qrCodeLibraryPromise
}

async function renderQrCodeDataUrl(text: string) {
  await loadQrCodeLibrary()

  if (typeof window === "undefined" || typeof window.QRCode !== "function") {
    throw new Error("Unable to load QRCode renderer")
  }

  const container = document.createElement("div")
  container.style.position = "fixed"
  container.style.left = "-9999px"
  container.style.top = "0"
  container.style.width = "320px"
  container.style.height = "320px"
  document.body.appendChild(container)

  try {
    new window.QRCode(container, {
      text,
      width: 320,
      height: 320,
      colorDark: "#0f172a",
      colorLight: "#ffffff",
      correctLevel: window.QRCode.CorrectLevel?.M,
    })

    const canvas = container.querySelector("canvas")
    if (canvas instanceof HTMLCanvasElement) {
      return canvas.toDataURL("image/png")
    }

    const image = container.querySelector("img")
    if (image instanceof HTMLImageElement && image.src) {
      return image.src
    }

    const svg = container.querySelector("svg")
    if (svg instanceof SVGElement) {
      const svgMarkup = new XMLSerializer().serializeToString(svg)
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`
    }

    throw new Error("QR renderer did not produce a printable asset")
  } finally {
    container.remove()
  }
}

function buildInstallerPacketQrPayload(
  packet: PlantationSirenInstallerPacket,
  endpointUrl: string
) {
  return JSON.stringify({
    apiKey: packet.apiKey,
    endpointPath: packet.endpointPath,
    endpointUrl,
    headerName: packet.headerName,
    kind: "safetyqr_plantation_siren_installer_packet_v1",
    preview: packet.preview,
    sirenId: packet.sirenId,
  })
}

function buildInstallerPacketPrintDocument(options: {
  displayName: string
  endpointUrl: string
  firstDeviceCheckInAt?: string | null
  headerLine: string
  onboardingHistory: Array<{ createdAt?: string | null; eventType?: string | null; note?: string | null }>
  plantationLabel: string
  provisioningLabel: string
  qrDataUrl: string
  qrPayload: string
  zoneLabel: string
}) {
  const historyMarkup = options.onboardingHistory.length
    ? options.onboardingHistory
        .map(
          (event) => `
            <li>
              <strong>${escapeHtml(titleizeToken(event.eventType || "event"))}</strong>
              <span> · ${escapeHtml(formatDateTime(event.createdAt))}</span>
              ${event.note ? `<div>${escapeHtml(event.note)}</div>` : ""}
            </li>
          `
        )
        .join("")
    : "<li>No onboarding history recorded yet.</li>"

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>SafetyQR Siren Installer Packet</title>
    <style>
      body { font-family: Inter, Arial, sans-serif; color: #0f172a; margin: 0; padding: 32px; }
      .sheet { border: 1px solid #dbe5f1; border-radius: 24px; padding: 28px; }
      .eyebrow { text-transform: uppercase; letter-spacing: 0.16em; font-size: 12px; color: #475569; }
      h1 { margin: 8px 0 0; font-size: 32px; }
      .lead { margin: 10px 0 0; font-size: 15px; line-height: 1.7; color: #475569; }
      .grid { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(240px, 320px); gap: 24px; margin-top: 24px; }
      .panel { border: 1px solid #e2e8f0; border-radius: 18px; padding: 18px; background: #f8fafc; }
      .panel h2 { margin: 0 0 10px; font-size: 16px; }
      .mono { word-break: break-word; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; line-height: 1.8; }
      .qr { width: 100%; border: 1px solid #e2e8f0; border-radius: 18px; padding: 16px; background: #ffffff; box-sizing: border-box; }
      .qr img { width: 100%; height: auto; display: block; }
      ol, ul { margin: 10px 0 0 20px; padding: 0; }
      li { margin: 8px 0; line-height: 1.6; }
      .meta { display: grid; gap: 8px; margin-top: 16px; }
      .meta-row { font-size: 14px; color: #334155; }
      .footer { margin-top: 22px; font-size: 12px; color: #64748b; }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="eyebrow">SafetyQR Installer Handoff</div>
      <h1>${escapeHtml(options.displayName)}</h1>
      <p class="lead">Use this packet to provision the siren heartbeat endpoint, trigger the first device check-in, and confirm the unit is live in the admin and ops desks.</p>
      <div class="meta">
        <div class="meta-row"><strong>Plantation:</strong> ${escapeHtml(options.plantationLabel)}</div>
        <div class="meta-row"><strong>Coverage:</strong> ${escapeHtml(options.zoneLabel)}</div>
        <div class="meta-row"><strong>Status:</strong> ${escapeHtml(options.provisioningLabel)}</div>
        <div class="meta-row"><strong>First check-in:</strong> ${escapeHtml(formatDateTime(options.firstDeviceCheckInAt))}</div>
      </div>
      <div class="grid">
        <div>
          <div class="panel">
            <h2>Provisioning values</h2>
            <div class="mono"><strong>Endpoint</strong><br />${escapeHtml(options.endpointUrl)}</div>
            <div class="mono" style="margin-top: 12px;"><strong>Header</strong><br />${escapeHtml(options.headerLine)}</div>
            <div class="mono" style="margin-top: 12px;"><strong>QR payload</strong><br />${escapeHtml(options.qrPayload)}</div>
          </div>
          <div class="panel" style="margin-top: 16px;">
            <h2>Installer checklist</h2>
            <ol>
              <li>Program the siren with the Siren ID and heartbeat header shown in this packet.</li>
              <li>Send one device heartbeat after the unit is powered and connected.</li>
              <li>Confirm the first heartbeat appears in admin and ops monitoring.</li>
            </ol>
          </div>
          <div class="panel" style="margin-top: 16px;">
            <h2>Onboarding history</h2>
            <ul>${historyMarkup}</ul>
          </div>
        </div>
        <div class="panel">
          <h2>QR provisioning packet</h2>
          <div class="qr">
            <img alt="Installer provisioning QR" src="${options.qrDataUrl}" />
          </div>
          <p class="footer">The QR encodes the installer packet payload, including the endpoint, header name, heartbeat key payload, and siren ID.</p>
        </div>
      </div>
    </div>
    <script>
      window.onload = function () {
        window.print();
      };
    </script>
  </body>
</html>`
}

function InstallerPacketQrCode(props: { payload: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [renderError, setRenderError] = useState<string | null>(null)

  useEffect(() => {
    let isActive = true

    async function renderQr() {
      const container = containerRef.current

      if (!container) {
        return
      }

      container.innerHTML = ""
      setRenderError(null)

      try {
        await loadQrCodeLibrary()

        if (!isActive || typeof window.QRCode !== "function") {
          return
        }

        new window.QRCode(container, {
          text: props.payload,
          width: 208,
          height: 208,
          colorDark: "#0f172a",
          colorLight: "#ffffff",
          correctLevel: window.QRCode.CorrectLevel?.M,
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        setRenderError(
          error instanceof Error ? error.message : "QR provisioning preview failed"
        )
      }
    }

    void renderQr()

    return () => {
      isActive = false
      if (containerRef.current) {
        containerRef.current.innerHTML = ""
      }
    }
  }, [props.payload])

  return (
    <div className="rounded-2xl border border-border/70 bg-background/80 px-3 py-3 text-sm">
      <p className="font-medium text-foreground">QR provisioning packet</p>
      <div className="mt-3 flex items-center justify-center rounded-2xl border border-border/70 bg-white p-4">
        <div
          aria-label="Installer provisioning QR"
          className="h-[208px] w-[208px]"
          ref={containerRef}
        />
      </div>
      {renderError ? (
        <p className="mt-2 text-xs text-muted-foreground">{renderError}</p>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          Scan to hand the packet to the installer without retyping the endpoint or key.
        </p>
      )}
    </div>
  )
}

function sortPlantationSirens(records: PlantationSirenRecord[]) {
  return [...records].sort((left, right) => {
    const leftOrder = left.escalationOrder ?? 0
    const rightOrder = right.escalationOrder ?? 0

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder
    }

    return (left.displayName || "").localeCompare(right.displayName || "")
  })
}

function formatSirenHealthLabel(state: string | null | undefined) {
  switch (state) {
    case "disabled_manual":
      return "Manually disabled"
    case "disabled_failure":
      return "Failure disabled"
    case "maintenance":
      return "Maintenance"
    default:
      return titleizeToken(state || "unknown")
  }
}

function resolveSirenHealthTone(state: string | null | undefined) {
  switch (state) {
    case "healthy":
      return "rounded-full bg-chart-3/15 px-3 py-1 text-xs font-medium text-foreground"
    case "degraded":
    case "stale":
      return "rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
    case "maintenance":
    case "disabled_manual":
    case "disabled_failure":
      return "rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive"
    default:
      return "rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground"
  }
}

function formatProvisioningStatusLabel(status: string | null | undefined) {
  switch (status) {
    case "confirmed":
      return "First check-in confirmed"
    case "awaiting_first_checkin":
      return "Awaiting first check-in"
    default:
      return "Provisioning pending"
  }
}

function resolveProvisioningStatusTone(status: string | null | undefined) {
  switch (status) {
    case "confirmed":
      return "rounded-full bg-chart-3/15 px-3 py-1 text-xs font-medium text-foreground"
    case "awaiting_first_checkin":
      return "rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
    default:
      return "rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground"
  }
}

function formatPilotStatus(value: string | null | undefined) {
  return value ? titleizeToken(value) : "Not set"
}

function getPilotTone(value: string | null | undefined) {
  const token = normalizeToken(value)

  if (token === "live" || token === "ready" || token === "active") {
    return "success"
  }

  if (token) {
    return "warning"
  }

  return "neutral"
}

function buildNotificationChannelList(organization: {
  contactEmail?: string | null
  contactPhone?: string | null
  notificationEmail?: string | null
  notificationPhone?: string | null
  notificationWhatsapp?: string | null
}) {
  return [
    organization.notificationEmail || organization.contactEmail ? "Email" : null,
    organization.notificationPhone || organization.contactPhone ? "SMS" : null,
    organization.notificationWhatsapp || organization.notificationPhone || organization.contactPhone
      ? "WhatsApp"
      : null,
  ].filter(Boolean) as string[]
}

function buildNotificationChannelForm(
  organization: Pick<
    OperatorOrganizationDetail,
    "notificationEmail" | "notificationPhone" | "notificationWhatsapp"
  > | null
): NotificationChannelFormState {
  if (!organization) {
    return INITIAL_NOTIFICATION_CHANNEL_FORM
  }

  return {
    notificationEmail: organization.notificationEmail ?? "",
    notificationPhone: organization.notificationPhone ?? "",
    notificationWhatsapp: organization.notificationWhatsapp ?? "",
  }
}

function buildPlantationPolicyForm(
  policy: PlantationPolicyRecord | null,
  plantationName: string | null
): PlantationPolicyFormState {
  if (!policy) {
    return {
      ...INITIAL_PLANTATION_POLICY_FORM,
      plantationName: plantationName ?? "",
    }
  }

  return {
    defaultLocalLanguageCode: policy.defaultLocalLanguageCode ?? "",
    dualLanguageEnabled: Boolean(policy.dualLanguageEnabled),
    notifyEmergencyContactsOnPanic: Boolean(policy.notifyEmergencyContactsOnPanic),
    notifyEmergencyContactsOnReport: Boolean(policy.notifyEmergencyContactsOnReport),
    notifyNearestAuthority: Boolean(policy.notifyNearestAuthority),
    panicSmsEnabled: Boolean(policy.panicSmsEnabled),
    panicSmsRecipientLimit: String(policy.panicSmsRecipientLimit ?? 2),
    plantationName: policy.plantationName ?? plantationName ?? "",
    sirenCooldownMinutes: String(policy.sirenCooldownMinutes ?? 5),
    sirenFailureDisableThreshold: String(policy.sirenFailureDisableThreshold ?? 3),
    sirenHeartbeatStaleMinutes: String(policy.sirenHeartbeatStaleMinutes ?? 180),
    sirenRoutingMode: policy.sirenRoutingMode ?? "",
    triggerSirensOnPanic: Boolean(policy.triggerSirensOnPanic),
  }
}

function getLookupPlantationId(record: PublicLookupData) {
  return (
    record.plantation?.plantationId
    ?? record.registration?.plantationId
    ?? null
  )
}

function getLookupPlantationName(record: PublicLookupData) {
  return (
    record.plantation?.plantationName
    ?? record.registration?.plantationName
    ?? null
  )
}

function getLookupZoneName(record: PublicLookupData) {
  return (
    record.plantation?.zoneName
    ?? record.physicalTag?.zoneName
    ?? record.registration?.zoneName
    ?? null
  )
}

function buildPlantationDirectory(
  fleetRegistrations: PublicLookupData[]
): PlantationDirectoryEntry[] {
  const directory = new Map<
    string,
    {
      plantationName: string | null
      preferredLanguageCode: string | null
      tagCount: number
      zones: Set<string>
    }
  >()

  for (const record of fleetRegistrations) {
    const plantationId = getLookupPlantationId(record)

    if (!plantationId) {
      continue
    }

    const existing =
      directory.get(plantationId)
      ?? {
        plantationName: null,
        preferredLanguageCode: null,
        tagCount: 0,
        zones: new Set<string>(),
      }
    const plantationName = getLookupPlantationName(record)
    const zoneName = getLookupZoneName(record)

    existing.plantationName ||= plantationName
    existing.preferredLanguageCode ||= record.registration?.preferredLanguageCode ?? null
    existing.tagCount += 1

    if (zoneName) {
      existing.zones.add(zoneName)
    }

    directory.set(plantationId, existing)
  }

  return Array.from(directory.entries())
    .map(([id, entry]) => ({
      id,
      plantationName: entry.plantationName || id,
      preferredLanguageCode: entry.preferredLanguageCode,
      tagCount: entry.tagCount,
      zoneCount: entry.zones.size,
      zones: Array.from(entry.zones).sort((left, right) => left.localeCompare(right)),
    }))
    .sort((left, right) => left.plantationName.localeCompare(right.plantationName))
}

async function loadAdminBundle(): Promise<AdminBundle> {
  const session = await resolveOperatorSession({
    requiredAccess: canAccessAdminWorkspace,
  })

  if (!canAccessAdminWorkspace(session)) {
    throw new Error("This session does not have access to the admin workspace.")
  }

  const isPortalAdmin = canManagePortalOrganizations(session)
  const scopedOrganizationId = session.organizationId || undefined
  const organizationPromise = isPortalAdmin
    ? apiClient.getOrganizations()
    : scopedOrganizationId
      ? apiClient.getOrganization(scopedOrganizationId)
      : Promise.resolve(null)

  const [organizationResult, cardsResult, fleetResult, stockResult] =
    await Promise.allSettled([
      organizationPromise,
      apiClient.getCards({
        organizationId: isPortalAdmin ? undefined : scopedOrganizationId,
      }),
      apiClient.getFleetInventory(500),
      apiClient.getB2cStockBatches({
        limit: 50,
        organizationId: isPortalAdmin ? undefined : scopedOrganizationId,
      }),
    ])

  if (organizationResult.status === "rejected") {
    throw organizationResult.reason
  }

  const cards =
    cardsResult.status === "fulfilled" ? (cardsResult.value.data ?? []) : []
  const fleetRegistrations =
    fleetResult.status === "fulfilled" ? (fleetResult.value.data ?? []) : []
  const stockBatches =
    stockResult.status === "fulfilled" ? (stockResult.value.data ?? []) : []

  if (isPortalAdmin) {
    return {
      cards,
      fleetRegistrations,
      organizations: (organizationResult.value?.data ?? []) as OperatorOrganizationRecord[],
      scopedOrganization: null,
      session,
      stockBatches,
    }
  }

  const scopedOrganization =
    organizationResult.value && "data" in organizationResult.value
      ? (organizationResult.value.data as OperatorOrganizationDetail)
      : null

  return {
    cards,
    fleetRegistrations,
    organizations: scopedOrganization ? [scopedOrganization] : [],
    scopedOrganization,
    session,
    stockBatches,
  }
}

function getScopedFleetRegistrations(
  fleetRegistrations: PublicLookupData[],
  organizationId: string | undefined
) {
  if (!organizationId) {
    return fleetRegistrations
  }

  return fleetRegistrations.filter((record) => {
    const recordOrganizationId =
      record.device?.organizationId ?? record.registration?.organizationId ?? null
    return recordOrganizationId === organizationId
  })
}

function getSectionValue(section: AdminDashboardSearch["section"]): AdminSection {
  return SECTION_OPTIONS.some((option) => option.value === section)
    ? (section as AdminSection)
    : "overview"
}

export function AdminWorkspace({
  onSearchChange,
  search,
}: AdminWorkspaceProps) {
  const [bundle, setBundle] = useState<AdminBundle | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedOrganization, setSelectedOrganization] =
    useState<OperatorOrganizationDetail | null>(null)
  const [selectedOrganizationError, setSelectedOrganizationError] =
    useState<string | null>(null)
  const [notificationChannelForm, setNotificationChannelForm] =
    useState<NotificationChannelFormState>(INITIAL_NOTIFICATION_CHANNEL_FORM)
  const [selectedPlantationId, setSelectedPlantationId] = useState("")
  const [plantationPolicy, setPlantationPolicy] =
    useState<PlantationPolicyRecord | null>(null)
  const [plantationPolicyForm, setPlantationPolicyForm] =
    useState<PlantationPolicyFormState>(INITIAL_PLANTATION_POLICY_FORM)
  const [plantationPolicyError, setPlantationPolicyError] =
    useState<string | null>(null)
  const [plantationSirens, setPlantationSirens] = useState<PlantationSirenRecord[]>([])
  const [plantationSirenError, setPlantationSirenError] = useState<string | null>(null)
  const [plantationSirenNotice, setPlantationSirenNotice] = useState<string | null>(null)
  const [plantationInstallerPacket, setPlantationInstallerPacket] =
    useState<PlantationSirenInstallerPacket | null>(null)
  const [plantationSirenForm, setPlantationSirenForm] =
    useState<PlantationSirenFormState>(INITIAL_PLANTATION_SIREN_FORM)
  const [isPlantationPolicyLoading, setIsPlantationPolicyLoading] = useState(false)
  const [isPlantationPolicySubmitting, setIsPlantationPolicySubmitting] =
    useState(false)
  const [isPlantationSirensLoading, setIsPlantationSirensLoading] = useState(false)
  const [isPlantationSirensSubmitting, setIsPlantationSirensSubmitting] =
    useState(false)
  const [cardsQuery, setCardsQuery] = useState("")
  const deferredCardsQuery = useDeferredValue(cardsQuery)
  const [organizationQuery, setOrganizationQuery] = useState("")
  const deferredOrganizationQuery = useDeferredValue(organizationQuery)
  const [statusFilter, setStatusFilter] = useState("all")
  const [createForm, setCreateForm] =
    useState<CreateOrganizationFormState>(INITIAL_CREATE_FORM)
  const section = getSectionValue(search.section)

  useEffect(() => {
    let isActive = true

    async function load() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const nextBundle = await loadAdminBundle()

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(nextBundle)
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the admin workspace."
          )
          setIsLoading(false)
        })
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [])

  const canManageOrganizations = Boolean(
    bundle && canManagePortalOrganizations(bundle.session)
  )
  const canAccessOrganizationSection = Boolean(
    bundle && (canManageOrganizations || bundle.scopedOrganization)
  )
  const selectedOrganizationId =
    section === "organizations"
      ? search.organizationId || bundle?.organizations[0]?.id
      : undefined

  useEffect(() => {
    let isActive = true

    async function loadSelectedOrganization() {
      if (!bundle || !canManageOrganizations || !selectedOrganizationId) {
        startTransition(() => {
          setSelectedOrganization(bundle?.scopedOrganization ?? null)
          setSelectedOrganizationError(null)
        })
        return
      }

      try {
        const response = await apiClient.getOrganization(selectedOrganizationId)

        if (!isActive) {
          return
        }

        startTransition(() => {
          setSelectedOrganization(response.data)
          setSelectedOrganizationError(null)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setSelectedOrganization(null)
          setSelectedOrganizationError(
            error instanceof Error
              ? error.message
              : "Failed to load organization detail."
          )
        })
      }
    }

    void loadSelectedOrganization()

    return () => {
      isActive = false
    }
  }, [bundle, canManageOrganizations, selectedOrganizationId])

  useEffect(() => {
    setNotificationChannelForm(buildNotificationChannelForm(selectedOrganization))
  }, [selectedOrganization])

  const selectedOrganizationScope = selectedOrganization ?? bundle?.scopedOrganization ?? null
  const selectedOrganizationFleetRegistrations = useMemo(() => {
    if (!bundle || !selectedOrganizationScope) {
      return []
    }

    return getScopedFleetRegistrations(
      bundle.fleetRegistrations,
      selectedOrganizationScope.id
    )
  }, [bundle, selectedOrganizationScope])
  const plantationDirectory = useMemo(
    () => buildPlantationDirectory(selectedOrganizationFleetRegistrations),
    [selectedOrganizationFleetRegistrations]
  )

  useEffect(() => {
    if (!plantationDirectory.length) {
      setSelectedPlantationId("")
      setPlantationPolicy(null)
      setPlantationPolicyForm(INITIAL_PLANTATION_POLICY_FORM)
      setPlantationPolicyError(null)
      setPlantationSirens([])
      setPlantationSirenError(null)
      setPlantationSirenNotice(null)
      setPlantationSirenForm(INITIAL_PLANTATION_SIREN_FORM)
      return
    }

    const hasCurrentSelection = plantationDirectory.some(
      (entry) => entry.id === selectedPlantationId
    )

    if (!hasCurrentSelection) {
      setSelectedPlantationId(plantationDirectory[0]?.id ?? "")
    }
  }, [plantationDirectory, selectedPlantationId])

  const selectedPlantation =
    plantationDirectory.find((entry) => entry.id === selectedPlantationId) ?? null

  useEffect(() => {
    let isActive = true

    async function loadPlantationPolicy() {
      if (!selectedOrganizationScope || !selectedPlantationId) {
        startTransition(() => {
          setPlantationPolicy(null)
          setPlantationPolicyForm(
            buildPlantationPolicyForm(null, selectedPlantation?.plantationName ?? null)
          )
          setPlantationPolicyError(null)
          setIsPlantationPolicyLoading(false)
        })
        return
      }

      startTransition(() => {
        setIsPlantationPolicyLoading(true)
        setPlantationPolicyError(null)
      })

      try {
        const response = await apiClient.getPlantationPolicy(selectedPlantationId, {
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
        })

        if (!isActive) {
          return
        }

        startTransition(() => {
          setPlantationPolicy(response.data)
          setPlantationPolicyForm(
            buildPlantationPolicyForm(
              response.data,
              selectedPlantation?.plantationName ?? null
            )
          )
          setPlantationPolicyError(null)
          setIsPlantationPolicyLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setPlantationPolicy(null)
          setPlantationPolicyForm(
            buildPlantationPolicyForm(null, selectedPlantation?.plantationName ?? null)
          )
          setPlantationPolicyError(
            error instanceof Error
              ? error.message
              : "Failed to load plantation policy."
          )
          setIsPlantationPolicyLoading(false)
        })
      }
    }

    void loadPlantationPolicy()

    return () => {
      isActive = false
    }
  }, [
    canManageOrganizations,
    selectedOrganizationScope,
    selectedPlantation,
    selectedPlantationId,
  ])

  useEffect(() => {
    let isActive = true

    async function loadPlantationSirens() {
      if (!selectedOrganizationScope || !selectedPlantationId) {
        startTransition(() => {
          setPlantationSirens([])
          setPlantationSirenError(null)
          setPlantationSirenNotice(null)
          setPlantationInstallerPacket(null)
          setPlantationSirenForm(INITIAL_PLANTATION_SIREN_FORM)
          setIsPlantationSirensLoading(false)
        })
        return
      }

      startTransition(() => {
        setIsPlantationSirensLoading(true)
        setPlantationSirenError(null)
        setPlantationSirenNotice(null)
      })

      try {
        const response = await apiClient.getPlantationSirens(selectedPlantationId, {
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
        })

        if (!isActive) {
          return
        }

        startTransition(() => {
          setPlantationSirens(response.data ?? [])
          setPlantationSirenError(null)
          setPlantationSirenNotice(null)
          setPlantationInstallerPacket((current) => {
            if (!current) {
              return null
            }

            const matchingSiren = (response.data ?? []).find(
              (item) => item.id === current.sirenId
            )

            return matchingSiren ? current : null
          })
          setIsPlantationSirensLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setPlantationSirens([])
          setPlantationSirenError(
            error instanceof Error
              ? error.message
              : "Failed to load plantation sirens."
          )
          setPlantationSirenNotice(null)
          setPlantationInstallerPacket(null)
          setIsPlantationSirensLoading(false)
        })
      }
    }

    void loadPlantationSirens()

    return () => {
      isActive = false
    }
  }, [canManageOrganizations, selectedOrganizationScope, selectedPlantationId])

  async function reloadPlantationSirens(options?: { announceCheckIn?: boolean }) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensLoading(true)
    setPlantationSirenError(null)

    try {
      const response = await apiClient.getPlantationSirens(selectedPlantationId, {
        organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
      })

      startTransition(() => {
        setPlantationSirens(response.data ?? [])
        setIsPlantationSirensLoading(false)

        if (options?.announceCheckIn && plantationInstallerPacket) {
          const matchingSiren = (response.data ?? []).find(
            (item) => item.id === plantationInstallerPacket.sirenId
          )

          if (matchingSiren?.firstDeviceCheckInAt) {
            setPlantationSirenNotice(
              `First device check-in confirmed for ${matchingSiren.displayName}.`
            )
          }
        }
      })
    } catch (error) {
      startTransition(() => {
        setPlantationSirenError(
          error instanceof Error
            ? error.message
            : "Failed to reload plantation sirens."
        )
        setIsPlantationSirensLoading(false)
      })
    }
  }

  const filteredCards = useMemo(() => {
    if (!bundle) {
      return []
    }

    const query = normalizeToken(deferredCardsQuery)
    if (!query) {
      return bundle.cards
    }

    return bundle.cards.filter((card) => {
      const haystack = [
        card.cardNumber,
        card.location?.name,
        card.location?.address,
        card.organization?.name,
      ]
        .map((value) => normalizeToken(value))
        .join(" ")

      return haystack.includes(query)
    })
  }, [bundle, deferredCardsQuery])

  const filteredOrganizations = useMemo(() => {
    if (!bundle) {
      return []
    }

    return bundle.organizations.filter((organization) => {
      if (statusFilter === "active" && !organization.isActive) {
        return false
      }

      if (statusFilter === "inactive" && organization.isActive) {
        return false
      }

      const query = normalizeToken(deferredOrganizationQuery)
      if (!query) {
        return true
      }

      const haystack = [
        organization.name,
        organization.city,
        organization.region,
        organization.type,
        organization.pilotStatus,
      ]
        .map((value) => normalizeToken(value))
        .join(" ")

      return haystack.includes(query)
    })
  }, [bundle, deferredOrganizationQuery, statusFilter])

  async function refreshWorkspace(nextOrganizationId?: string) {
    const nextBundle = await loadAdminBundle()

    startTransition(() => {
      setBundle(nextBundle)
      setErrorMessage(null)
    })

    if (nextOrganizationId) {
      onSearchChange({
        organizationId: nextOrganizationId,
        section: "organizations",
      })
    }
  }

  async function handleCreateOrganization(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!bundle || !canManageOrganizations) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const created = await apiClient.createOrganization({
        approximateBlockCount: createForm.approximateBlockCount.trim()
          ? Number.parseInt(createForm.approximateBlockCount, 10)
          : null,
        approximateRowCount: createForm.approximateRowCount.trim()
          ? Number.parseInt(createForm.approximateRowCount, 10)
          : null,
        approximateTreeCount: createForm.approximateTreeCount.trim()
          ? Number.parseInt(createForm.approximateTreeCount, 10)
          : null,
        billingEmail: createForm.billingEmail.trim() || null,
        city: createForm.city.trim(),
        contactEmail: createForm.contactEmail.trim() || null,
        contactPhone: createForm.contactPhone.trim() || null,
        customerCategory: createForm.customerCategory.trim() || null,
        defaultYieldUnit: createForm.defaultYieldUnit.trim() || null,
        fleetCategory: createForm.fleetCategory.trim() || null,
        fleetEnabled: createForm.fleetEnabled,
        fleetSize: createForm.fleetSize.trim()
          ? Number.parseInt(createForm.fleetSize, 10)
          : null,
        goLiveTargetDate: createForm.goLiveTargetDate || null,
        implementationMode: createForm.implementationMode || null,
        legalName: createForm.legalName.trim() || null,
        name: createForm.name.trim(),
        notificationEmail: createForm.notificationEmail.trim() || null,
        notificationPhone: createForm.notificationPhone.trim() || null,
        notificationWhatsapp: createForm.notificationWhatsapp.trim() || null,
        onboardingOwnerInternal: createForm.onboardingOwnerInternal.trim() || null,
        operatingDistrict: createForm.operatingDistrict.trim() || null,
        operatingPostalCode: createForm.operatingPostalCode.trim() || null,
        operatingState: createForm.operatingState.trim() || null,
        operatingTaluk: createForm.operatingTaluk.trim() || null,
        operatingVillage: createForm.operatingVillage.trim() || null,
        orchardCount: createForm.orchardCount.trim()
          ? Number.parseInt(createForm.orchardCount, 10)
          : null,
        pilotStartedAt: createForm.pilotStartedAt || null,
        pilotStatus: createForm.pilotStatus.trim() || null,
        primaryContactName: createForm.primaryContactName.trim() || null,
        primaryCropType: createForm.primaryCropType.trim() || null,
        primaryOwnerDesignation: createForm.primaryOwnerDesignation.trim() || null,
        primaryOwnerEmail: createForm.primaryOwnerEmail.trim() || null,
        primaryOwnerName: createForm.primaryOwnerName.trim() || null,
        primaryOwnerPhone: createForm.primaryOwnerPhone.trim() || null,
        primaryOwnerPreferredLanguage:
          createForm.primaryOwnerPreferredLanguage.trim() || null,
        primaryVariety: createForm.primaryVariety.trim() || null,
        qrRolloutRequired: createForm.qrRolloutRequired,
        region: createForm.region.trim() || null,
        treeImportMode: createForm.treeImportMode || null,
        type: createForm.type.trim(),
      })

      startTransition(() => {
        setCreateForm(INITIAL_CREATE_FORM)
      })

      await refreshWorkspace(created.data.id)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to create the organization."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleToggleOrganizationStatus(
    organization: OperatorOrganizationRecord | OperatorOrganizationDetail
  ) {
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      await apiClient.updateOrganization(organization.id, {
        isActive: !organization.isActive,
      })

      await refreshWorkspace(organization.id)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to update organization status."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleUpdateNotificationChannels(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedOrganization) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      await apiClient.updateOrganization(selectedOrganization.id, {
        notificationEmail: notificationChannelForm.notificationEmail.trim() || null,
        notificationPhone: notificationChannelForm.notificationPhone.trim() || null,
        notificationWhatsapp: notificationChannelForm.notificationWhatsapp.trim() || null,
      })

      await refreshWorkspace(selectedOrganization.id)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to update organization notification channels."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleSavePlantationPolicy(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationPolicySubmitting(true)
    setPlantationPolicyError(null)

    try {
      const response = await apiClient.updatePlantationPolicy(selectedPlantationId, {
        defaultLocalLanguageCode:
          plantationPolicyForm.defaultLocalLanguageCode || null,
        dualLanguageEnabled: plantationPolicyForm.dualLanguageEnabled,
        notifyEmergencyContactsOnPanic:
          plantationPolicyForm.notifyEmergencyContactsOnPanic,
        notifyEmergencyContactsOnReport:
          plantationPolicyForm.notifyEmergencyContactsOnReport,
        notifyNearestAuthority: plantationPolicyForm.notifyNearestAuthority,
        organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
        panicSmsEnabled: plantationPolicyForm.panicSmsEnabled,
        panicSmsRecipientLimit: Number.parseInt(
          plantationPolicyForm.panicSmsRecipientLimit,
          10
        ),
        plantationName: plantationPolicyForm.plantationName.trim() || null,
        sirenCooldownMinutes: Number.parseInt(
          plantationPolicyForm.sirenCooldownMinutes,
          10
        ),
        sirenFailureDisableThreshold: Number.parseInt(
          plantationPolicyForm.sirenFailureDisableThreshold,
          10
        ),
        sirenHeartbeatStaleMinutes: Number.parseInt(
          plantationPolicyForm.sirenHeartbeatStaleMinutes,
          10
        ),
        sirenRoutingMode: plantationPolicyForm.sirenRoutingMode || null,
        triggerSirensOnPanic: plantationPolicyForm.triggerSirensOnPanic,
      })

      startTransition(() => {
        setPlantationPolicy(response.data)
        setPlantationPolicyForm(
          buildPlantationPolicyForm(
            response.data,
            selectedPlantation?.plantationName ?? null
          )
        )
        setPlantationPolicyError(null)
      })
    } catch (error) {
      setPlantationPolicyError(
        error instanceof Error
          ? error.message
          : "Failed to save plantation policy."
      )
    } finally {
      setIsPlantationPolicySubmitting(false)
    }
  }

  function buildInstallerPacketFromSiren(
    siren: PlantationSirenRecord
  ): PlantationSirenInstallerPacket | null {
    if (!siren.heartbeatProvisioning?.apiKey) {
      return null
    }

    return {
      apiKey: siren.heartbeatProvisioning.apiKey,
      displayName: siren.displayName,
      endpointPath: siren.heartbeatProvisioning.endpointPath,
      generatedAt: new Date().toISOString(),
      headerName: siren.heartbeatProvisioning.headerName,
      preview: siren.heartbeatProvisioning.preview,
      sirenId: siren.id,
    }
  }

  async function handleCreatePlantationSiren(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      const response = await apiClient.createPlantationSiren(selectedPlantationId, {
        displayName: plantationSirenForm.displayName.trim() || null,
        escalationOrder: Number.parseInt(plantationSirenForm.escalationOrder, 10) || 0,
        latitude: plantationSirenForm.latitude.trim()
          ? Number.parseFloat(plantationSirenForm.latitude)
          : null,
        locationLabel: plantationSirenForm.locationLabel.trim() || null,
        longitude: plantationSirenForm.longitude.trim()
          ? Number.parseFloat(plantationSirenForm.longitude)
          : null,
        organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
        phoneNumber: plantationSirenForm.phoneNumber.trim(),
        plantationName: plantationPolicyForm.plantationName.trim() || selectedPlantation?.plantationName || null,
        triggerOnPanic: plantationSirenForm.triggerOnPanic,
        zoneName: plantationSirenForm.zoneName.trim() || null,
      })

      startTransition(() => {
        setPlantationSirens((current) => sortPlantationSirens([...current, response.data]))
        setPlantationSirenForm(INITIAL_PLANTATION_SIREN_FORM)
        setPlantationInstallerPacket(buildInstallerPacketFromSiren(response.data))
        setPlantationSirenNotice(
          response.data.heartbeatProvisioning?.apiKey
            ? `Installer packet ready for ${response.data.displayName}.`
            : null
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error
          ? error.message
          : "Failed to create plantation siren."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  async function handleTogglePlantationSiren(siren: PlantationSirenRecord) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      const response = await apiClient.updatePlantationSiren(
        selectedPlantationId,
        siren.id,
        {
          displayName: siren.displayName,
          escalationOrder: siren.escalationOrder,
          isActive: !siren.isActive,
          latitude: siren.latitude ?? null,
          locationLabel: siren.locationLabel || null,
          longitude: siren.longitude ?? null,
          maintenanceMode: siren.maintenanceMode,
          maintenanceReason: siren.maintenanceReason || null,
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
          phoneNumber: siren.phoneNumber || "",
          plantationName: siren.plantationName || selectedPlantation?.plantationName || null,
          scheduledMaintenanceEndAt: siren.scheduledMaintenanceEndAt || null,
          scheduledMaintenanceNote: siren.scheduledMaintenanceNote || null,
          scheduledMaintenanceStartAt: siren.scheduledMaintenanceStartAt || null,
          triggerOnPanic: siren.triggerOnPanic,
          zoneName: siren.zoneName || null,
        }
      )

      startTransition(() => {
        setPlantationSirens((current) =>
          current.map((item) => (item.id === siren.id ? response.data : item))
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error
          ? error.message
          : "Failed to update plantation siren."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  async function handleDeletePlantationSiren(siren: PlantationSirenRecord) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      await apiClient.deletePlantationSiren(selectedPlantationId, siren.id, {
        organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
      })

      startTransition(() => {
        setPlantationSirens((current) =>
          current.filter((item) => item.id !== siren.id)
        )
        setPlantationInstallerPacket((current) =>
          current?.sirenId === siren.id ? null : current
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error
          ? error.message
          : "Failed to delete plantation siren."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  async function handleRecordPlantationSirenHeartbeat(siren: PlantationSirenRecord) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      const response = await apiClient.recordPlantationSirenHeartbeat(
        selectedPlantationId,
        siren.id,
        {
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
        }
      )

      startTransition(() => {
        setPlantationSirens((current) =>
          sortPlantationSirens(
            current.map((item) => (item.id === siren.id ? response.data : item))
          )
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error ? error.message : "Failed to record siren heartbeat."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  async function handleSetPlantationSirenMaintenance(
    siren: PlantationSirenRecord,
    enabled: boolean
  ) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      const response = await apiClient.setPlantationSirenMaintenance(
        selectedPlantationId,
        siren.id,
        {
          enabled,
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
          reason: enabled
            ? `Operator ${enabled ? "maintenance" : "service"} mode`
            : null,
        }
      )

      startTransition(() => {
        setPlantationSirens((current) =>
          sortPlantationSirens(
            current.map((item) => (item.id === siren.id ? response.data : item))
          )
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error
          ? error.message
          : "Failed to update siren maintenance state."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  async function handleRestorePlantationSirenService(siren: PlantationSirenRecord) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      const response = await apiClient.restorePlantationSirenService(
        selectedPlantationId,
        siren.id,
        {
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
        }
      )

      startTransition(() => {
        setPlantationSirens((current) =>
          sortPlantationSirens(
            current.map((item) => (item.id === siren.id ? response.data : item))
          )
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error ? error.message : "Failed to restore siren service."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  async function handleRotatePlantationSirenHeartbeatKey(siren: PlantationSirenRecord) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      const response = await apiClient.rotatePlantationSirenHeartbeatKey(
        selectedPlantationId,
        siren.id,
        {
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
        }
      )

      startTransition(() => {
        setPlantationSirens((current) =>
          sortPlantationSirens(
            current.map((item) => (item.id === siren.id ? response.data : item))
          )
        )
        setPlantationInstallerPacket(buildInstallerPacketFromSiren(response.data))
        setPlantationSirenNotice(
          response.data.heartbeatProvisioning?.apiKey
            ? `Fresh installer packet ready for ${response.data.displayName}.`
            : null
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error ? error.message : "Failed to rotate siren heartbeat key."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  async function handleSchedulePlantationSirenMaintenance(
    siren: PlantationSirenRecord,
    schedule: { note?: string | null; scheduledEndAt: string; scheduledStartAt: string }
  ) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      const response = await apiClient.schedulePlantationSirenMaintenance(
        selectedPlantationId,
        siren.id,
        {
          note: schedule.note || null,
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
          scheduledEndAt: schedule.scheduledEndAt,
          scheduledStartAt: schedule.scheduledStartAt,
        }
      )

      startTransition(() => {
        setPlantationSirens((current) =>
          sortPlantationSirens(
            current.map((item) => (item.id === siren.id ? response.data : item))
          )
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error ? error.message : "Failed to schedule siren maintenance."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  async function handleAddPlantationSirenMaintenanceNote(
    siren: PlantationSirenRecord,
    note: string
  ) {
    if (!selectedOrganizationScope || !selectedPlantationId) {
      return
    }

    setIsPlantationSirensSubmitting(true)
    setPlantationSirenError(null)
    setPlantationSirenNotice(null)

    try {
      const response = await apiClient.addPlantationSirenMaintenanceNote(
        selectedPlantationId,
        siren.id,
        {
          note,
          organizationId: canManageOrganizations ? selectedOrganizationScope.id : undefined,
        }
      )

      startTransition(() => {
        setPlantationSirens((current) =>
          sortPlantationSirens(
            current.map((item) => (item.id === siren.id ? response.data : item))
          )
        )
      })
    } catch (error) {
      setPlantationSirenError(
        error instanceof Error ? error.message : "Failed to save the maintenance note."
      )
    } finally {
      setIsPlantationSirensSubmitting(false)
    }
  }

  return (
    <>
      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}

      {isLoading ? <AdminWorkspaceLoadingState /> : null}

      {!isLoading && !bundle && !errorMessage ? (
        <OperatorPanel
          title="Admin access restricted"
          description="This page is only available to sessions with admin access."
        >
          <OperatorStatusMessage tone="danger">
            Sign in again with an admin account to open this workspace.
          </OperatorStatusMessage>
        </OperatorPanel>
      ) : null}

      {!isLoading && bundle ? (
        <>
          <OperatorPanel
            title="Workspace sections"
            description="Switch between the overview, card visibility, and organization management sections."
          >
            <div className="flex flex-wrap gap-2">
              {SECTION_OPTIONS.map((option) => {
                const disabled =
                  option.value === "organizations" && !canAccessOrganizationSection

                return (
                  <Button
                    key={option.value}
                    disabled={disabled}
                    variant={section === option.value ? "default" : "outline"}
                    onClick={() => {
                      onSearchChange({
                        organizationId:
                          option.value === "organizations"
                            ? selectedOrganizationId
                            : undefined,
                        section: option.value,
                      })
                    }}
                  >
                    {option.label}
                  </Button>
                )
              })}
            </div>
          </OperatorPanel>

          {section === "overview" ? (
            <>
              <OperatorPanel
                title="Readiness posture"
                description="Review organization readiness, contacts, and launch status."
              >
                {bundle.scopedOrganization ? (
                  <ScopedOrganizationSummary
                    fleetRegistrations={getScopedFleetRegistrations(
                      bundle.fleetRegistrations,
                      bundle.scopedOrganization.id
                    )}
                    organization={bundle.scopedOrganization}
                  />
                ) : (
                  <PortalOrganizationSummary organizations={bundle.organizations} />
                )}
              </OperatorPanel>

              <OperatorPanel
                title="Card visibility"
                description="Review deployed cards visible in the current admin scope."
              >
                <div className="grid gap-3">
                  {bundle.cards.slice(0, 6).map((card) => (
                    <div
                      key={card.cardNumber}
                      className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <strong>Card #{card.cardNumber}</strong>
                        <span className="text-sm text-muted-foreground">
                          {card.organization?.name || "Portal scope"}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {card.location?.name || "Unassigned location"} · deployed{" "}
                        {formatDateTime(card.deployedAt)}
                      </p>
                    </div>
                  ))}
                </div>
              </OperatorPanel>

              <OperatorPanel
                title="Recent stock activity"
                description="Review the latest B2C stock batches visible in the current admin scope."
              >
                {bundle.stockBatches.length ? (
                  <div className="grid gap-3">
                    {bundle.stockBatches.slice(0, 3).map((batch) => (
                      <div
                        key={batch.id}
                        className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">{batch.batchCode}</span>
                          <span className="text-sm text-muted-foreground">
                            {titleizeToken(batch.lifecycleState)} · {batch.quantity} tags
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <OperatorEmptyState
                    title="No recent stock activity in scope."
                    message="No B2C stock batches are visible in the current admin scope."
                  />
                )}
              </OperatorPanel>
            </>
          ) : null}

          {section === "cards" ? (
            <OperatorPanel
              title="Card visibility"
              description="Search and review deployed cards in the current admin scope."
            >
              <div className="grid gap-4">
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem]">
                  <label className="grid gap-2 text-sm">
                    <span className="font-medium">Search cards</span>
                    <input
                      className={INPUT_CLASS}
                      placeholder="Card number, location, or organization"
                      value={cardsQuery}
                      onChange={(event) => setCardsQuery(event.target.value)}
                    />
                  </label>
                  <div className="rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm text-muted-foreground">
                    {filteredCards.length} card{filteredCards.length === 1 ? "" : "s"} visible
                  </div>
                </div>

                {filteredCards.length ? (
                  <div className="grid gap-3">
                    {filteredCards.map((card) => (
                      <div
                        key={card.cardNumber}
                        className="grid gap-2 rounded-3xl border border-border/70 bg-background/65 px-4 py-4 md:grid-cols-[10rem_minmax(0,1fr)_14rem]"
                      >
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            Card
                          </p>
                          <strong className="text-lg">{card.cardNumber}</strong>
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {card.location?.name || "Unassigned location"}
                          </p>
                          <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            {card.organization?.name || "Portal scope"} ·{" "}
                            {card.location?.address || "No address recorded"}
                          </p>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <p>Deployed {formatDateTime(card.deployedAt)}</p>
                          <p className="mt-1">Updated {formatDateTime(card.updatedAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <OperatorEmptyState
                    title="No cards matched the current filter."
                    message="Try another search to review card activity in this workspace."
                  />
                )}
              </div>
            </OperatorPanel>
          ) : null}

          {section === "organizations" ? (
            canAccessOrganizationSection ? (
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(24rem,0.95fr)]">
                <OperatorPanel
                  title={
                    canManageOrganizations
                      ? "Portal organization management"
                      : "Scoped organization context"
                  }
                  description={
                    canManageOrganizations
                      ? "Create, review, and manage organizations from one place."
                      : "Review the current organization scope before editing plantation policy controls."
                  }
                >
                  {canManageOrganizations ? (
                    <form className="grid gap-4" onSubmit={handleCreateOrganization}>
                      <div className="grid gap-3">
                        <div>
                          <p className="text-sm font-medium">Organization basics</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Create the tenant, then attach the first owner and orchard rollout profile in the same pass.
                          </p>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Organization name</span>
                            <input
                              aria-label="Organization name"
                              className={INPUT_CLASS}
                              required
                              value={createForm.name}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  name: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Legal name</span>
                            <input
                              aria-label="Legal name"
                              className={INPUT_CLASS}
                              value={createForm.legalName}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  legalName: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Type</span>
                            <select
                              aria-label="Type"
                              className={INPUT_CLASS}
                              value={createForm.type}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  type: event.target.value,
                                }))
                              }
                            >
                              <option value="traffic_police">Traffic police</option>
                              <option value="fleet_operator">Fleet operator</option>
                              <option value="insurance_partner">Insurance partner</option>
                              <option value="municipality">Municipality</option>
                              <option value="orchard_operator">Orchard operator</option>
                            </select>
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Customer category</span>
                            <input
                              aria-label="Customer category"
                              className={INPUT_CLASS}
                              placeholder="owner, estate, cooperative"
                              value={createForm.customerCategory}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  customerCategory: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">City</span>
                            <input
                              aria-label="City"
                              className={INPUT_CLASS}
                              required
                              value={createForm.city}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  city: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Region</span>
                            <input
                              aria-label="Region"
                              className={INPUT_CLASS}
                              value={createForm.region}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  region: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Operating village</span>
                            <input
                              aria-label="Operating village"
                              className={INPUT_CLASS}
                              value={createForm.operatingVillage}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  operatingVillage: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Operating taluk</span>
                            <input
                              aria-label="Operating taluk"
                              className={INPUT_CLASS}
                              value={createForm.operatingTaluk}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  operatingTaluk: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Operating district</span>
                            <input
                              aria-label="Operating district"
                              className={INPUT_CLASS}
                              value={createForm.operatingDistrict}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  operatingDistrict: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Operating state</span>
                            <input
                              aria-label="Operating state"
                              className={INPUT_CLASS}
                              value={createForm.operatingState}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  operatingState: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Operating postal code</span>
                            <input
                              aria-label="Operating postal code"
                              className={INPUT_CLASS}
                              value={createForm.operatingPostalCode}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  operatingPostalCode: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">GrowBetter onboarding lead</span>
                            <input
                              aria-label="GrowBetter onboarding lead"
                              className={INPUT_CLASS}
                              placeholder="Implementation manager"
                              value={createForm.onboardingOwnerInternal}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  onboardingOwnerInternal: event.target.value,
                                }))
                              }
                            />
                          </label>
                        </div>
                      </div>

                      <div className="grid gap-3 rounded-3xl border border-border/70 bg-background/55 px-4 py-4">
                        <div>
                          <p className="text-sm font-medium">Primary orchard admin access</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Provision the customer-side orchard admin now. A Clerk invite email will be sent after the tenant is created.
                          </p>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Primary orchard admin name</span>
                            <input
                              aria-label="Primary orchard admin name"
                              className={INPUT_CLASS}
                              value={createForm.primaryOwnerName}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  primaryOwnerName: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Primary orchard admin email</span>
                            <input
                              aria-label="Primary orchard admin email"
                              className={INPUT_CLASS}
                              type="email"
                              value={createForm.primaryOwnerEmail}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  primaryOwnerEmail: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Primary orchard admin phone</span>
                            <input
                              aria-label="Primary orchard admin phone"
                              className={INPUT_CLASS}
                              value={createForm.primaryOwnerPhone}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  primaryOwnerPhone: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Primary orchard admin designation</span>
                            <input
                              aria-label="Primary orchard admin designation"
                              className={INPUT_CLASS}
                              value={createForm.primaryOwnerDesignation}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  primaryOwnerDesignation: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Preferred language</span>
                            <input
                              aria-label="Primary orchard admin preferred language"
                              className={INPUT_CLASS}
                              value={createForm.primaryOwnerPreferredLanguage}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  primaryOwnerPreferredLanguage: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Primary contact name</span>
                            <input
                              aria-label="Primary contact name"
                              className={INPUT_CLASS}
                              value={createForm.primaryContactName}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  primaryContactName: event.target.value,
                                }))
                              }
                            />
                          </label>
                        </div>
                      </div>

                      <div className="grid gap-3 rounded-3xl border border-border/70 bg-background/55 px-4 py-4">
                        <div>
                          <p className="text-sm font-medium">Commercial and notification profile</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Capture who manages billing and fallback alerting while the orchard admin completes their own setup.
                          </p>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Contact email</span>
                            <input
                              aria-label="Contact email"
                              className={INPUT_CLASS}
                              type="email"
                              value={createForm.contactEmail}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  contactEmail: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Contact phone</span>
                            <input
                              aria-label="Contact phone"
                              className={INPUT_CLASS}
                              value={createForm.contactPhone}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  contactPhone: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Billing email</span>
                            <input
                              aria-label="Billing email"
                              className={INPUT_CLASS}
                              type="email"
                              value={createForm.billingEmail}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  billingEmail: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Notification email</span>
                            <input
                              aria-label="Notification email"
                              className={INPUT_CLASS}
                              type="email"
                              value={createForm.notificationEmail}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  notificationEmail: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Notification phone</span>
                            <input
                              aria-label="Notification phone"
                              className={INPUT_CLASS}
                              value={createForm.notificationPhone}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  notificationPhone: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Notification WhatsApp</span>
                            <input
                              aria-label="Notification WhatsApp"
                              className={INPUT_CLASS}
                              value={createForm.notificationWhatsapp}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  notificationWhatsapp: event.target.value,
                                }))
                              }
                            />
                          </label>
                        </div>
                      </div>

                      <div className="grid gap-3 rounded-3xl border border-border/70 bg-background/55 px-4 py-4">
                        <div>
                          <p className="text-sm font-medium">Orchard rollout baseline</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Store the initial operational assumptions that the first-time orchard wizard will refine later.
                          </p>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Primary crop type</span>
                            <input
                              aria-label="Primary crop type"
                              className={INPUT_CLASS}
                              value={createForm.primaryCropType}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  primaryCropType: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Primary variety</span>
                            <input
                              aria-label="Primary variety"
                              className={INPUT_CLASS}
                              value={createForm.primaryVariety}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  primaryVariety: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Orchard count</span>
                            <input
                              aria-label="Orchard count"
                              className={INPUT_CLASS}
                              inputMode="numeric"
                              value={createForm.orchardCount}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  orchardCount: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Approximate tree count</span>
                            <input
                              aria-label="Approximate tree count"
                              className={INPUT_CLASS}
                              inputMode="numeric"
                              value={createForm.approximateTreeCount}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  approximateTreeCount: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Approximate block count</span>
                            <input
                              aria-label="Approximate block count"
                              className={INPUT_CLASS}
                              inputMode="numeric"
                              value={createForm.approximateBlockCount}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  approximateBlockCount: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Approximate row count</span>
                            <input
                              aria-label="Approximate row count"
                              className={INPUT_CLASS}
                              inputMode="numeric"
                              value={createForm.approximateRowCount}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  approximateRowCount: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Implementation mode</span>
                            <select
                              aria-label="Implementation mode"
                              className={INPUT_CLASS}
                              value={createForm.implementationMode}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  implementationMode: event.target.value,
                                }))
                              }
                            >
                              <option value="growbetter_assisted">GrowBetter assisted</option>
                              <option value="hybrid">Hybrid rollout</option>
                              <option value="owner_self_serve">Orchard admin self-serve</option>
                            </select>
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Tree import mode</span>
                            <select
                              aria-label="Tree import mode"
                              className={INPUT_CLASS}
                              value={createForm.treeImportMode}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  treeImportMode: event.target.value,
                                }))
                              }
                            >
                              <option value="assisted_csv">Assisted CSV import</option>
                              <option value="manual_registry">Manual registry</option>
                              <option value="owner_csv">Orchard admin CSV import</option>
                            </select>
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Default yield unit</span>
                            <input
                              aria-label="Default yield unit"
                              className={INPUT_CLASS}
                              value={createForm.defaultYieldUnit}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  defaultYieldUnit: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Go-live target date</span>
                            <input
                              aria-label="Go-live target date"
                              className={INPUT_CLASS}
                              type="date"
                              value={createForm.goLiveTargetDate}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  goLiveTargetDate: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                            <input
                              aria-label="QR rollout required"
                              checked={createForm.qrRolloutRequired}
                              type="checkbox"
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  qrRolloutRequired: event.target.checked,
                                }))
                              }
                            />
                            <span>QR rollout required</span>
                          </label>
                        </div>
                      </div>

                      <div className="grid gap-3 rounded-3xl border border-border/70 bg-background/55 px-4 py-4">
                        <div>
                          <p className="text-sm font-medium">Legacy ops settings</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Preserve the current organization controls used by the existing operations stack.
                          </p>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Pilot status</span>
                            <input
                              aria-label="Pilot status"
                              className={INPUT_CLASS}
                              placeholder="active, pilot, ready"
                              value={createForm.pilotStatus}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  pilotStatus: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Pilot start</span>
                            <input
                              aria-label="Pilot start"
                              className={INPUT_CLASS}
                              type="date"
                              value={createForm.pilotStartedAt}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  pilotStartedAt: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Fleet category</span>
                            <input
                              aria-label="Fleet category"
                              className={INPUT_CLASS}
                              value={createForm.fleetCategory}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  fleetCategory: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Fleet size</span>
                            <input
                              aria-label="Fleet size"
                              className={INPUT_CLASS}
                              inputMode="numeric"
                              value={createForm.fleetSize}
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  fleetSize: event.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                            <input
                              aria-label="Fleet enabled"
                              checked={createForm.fleetEnabled}
                              type="checkbox"
                              onChange={(event) =>
                                setCreateForm((previous) => ({
                                  ...previous,
                                  fleetEnabled: event.target.checked,
                                }))
                              }
                            />
                            <span>Fleet enabled</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <Button disabled={isSubmitting} type="submit">
                          {isSubmitting ? "Creating…" : "Create organization"}
                        </Button>
                        <Button
                          disabled={isSubmitting}
                          type="button"
                          variant="outline"
                          onClick={() => setCreateForm(INITIAL_CREATE_FORM)}
                        >
                          Reset form
                        </Button>
                      </div>
                    </form>
                  ) : selectedOrganizationScope ? (
                    <ScopedOrganizationSummary
                      fleetRegistrations={selectedOrganizationFleetRegistrations}
                      organization={selectedOrganizationScope}
                    />
                  ) : (
                    <OperatorEmptyState
                      title="No scoped organization available."
                      message="Sign in with an organization-scoped admin account to edit plantation policy controls."
                    />
                  )}

                  {canManageOrganizations ? (
                    <div className="mt-6 grid gap-4">
                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_11rem]">
                        <label className="grid gap-2 text-sm">
                          <span className="font-medium">Filter organizations</span>
                          <input
                            className={INPUT_CLASS}
                            placeholder="Name, city, region, or pilot state"
                            value={organizationQuery}
                            onChange={(event) => setOrganizationQuery(event.target.value)}
                          />
                        </label>
                        <label className="grid gap-2 text-sm">
                          <span className="font-medium">Status</span>
                          <select
                            className={INPUT_CLASS}
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                          >
                            <option value="all">All</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </label>
                      </div>

                      <div className="grid gap-3">
                        {filteredOrganizations.map((organization) => (
                          <button
                            key={organization.id}
                            className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4 text-left transition-colors hover:border-primary/35 hover:bg-background/80"
                            type="button"
                            onClick={() =>
                              onSearchChange({
                                organizationId: organization.id,
                                section: "organizations",
                              })
                            }
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="font-semibold">{organization.name}</p>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  {[titleizeToken(organization.type), organization.city, organization.region]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                                  {organization.isActive ? "Active" : "Inactive"}
                                </span>
                                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                                  {formatPilotStatus(organization.pilotStatus)}
                                </span>
                              </div>
                            </div>
                            <p className="mt-3 text-sm text-muted-foreground">
                              {organization.userCount || 0} users · {organization.authorityCount || 0} authorities ·{" "}
                              {organization.locationCount || 0} locations · {organization.cardCount || 0} cards
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </OperatorPanel>

                <OperatorPanel
                  title="Selected organization"
                  description="Review the selected organization's readiness, contacts, locations, and authorities."
                >
                  {selectedOrganizationError ? (
                    <OperatorStatusMessage tone="danger">
                      {selectedOrganizationError}
                    </OperatorStatusMessage>
                  ) : null}

                  {selectedOrganization ? (
                    <PortalOrganizationDetail
                      isSubmitting={isSubmitting}
                      notificationChannelForm={notificationChannelForm}
                      onNotificationChannelChange={(key, value) =>
                        setNotificationChannelForm((currentForm) => ({
                          ...currentForm,
                          [key]: value,
                        }))
                      }
                      onResetNotificationChannels={() =>
                        setNotificationChannelForm(
                          buildNotificationChannelForm(selectedOrganization)
                        )
                      }
                      onSaveNotificationChannels={handleUpdateNotificationChannels}
                      onPlantationPolicyChange={(key, value) =>
                        setPlantationPolicyForm((currentForm) => ({
                          ...currentForm,
                          [key]: value,
                        }))
                      }
                      onResetPlantationPolicy={() =>
                        setPlantationPolicyForm(
                          buildPlantationPolicyForm(
                            plantationPolicy,
                            selectedPlantation?.plantationName ?? null
                          )
                        )
                      }
                      onSavePlantationPolicy={handleSavePlantationPolicy}
                      onCreatePlantationSiren={handleCreatePlantationSiren}
                      onDeletePlantationSiren={handleDeletePlantationSiren}
                      onPlantationSirenHeartbeat={handleRecordPlantationSirenHeartbeat}
                      onPlantationSirenMaintenance={handleSetPlantationSirenMaintenance}
                      onPlantationSirenFormChange={(key, value) =>
                        setPlantationSirenForm((currentForm) => ({
                          ...currentForm,
                          [key]: value,
                        }))
                      }
                      onRotatePlantationSirenHeartbeatKey={
                        handleRotatePlantationSirenHeartbeatKey
                      }
                      onSchedulePlantationSirenMaintenance={
                        handleSchedulePlantationSirenMaintenance
                      }
                      onAddPlantationSirenMaintenanceNote={
                        handleAddPlantationSirenMaintenanceNote
                      }
                      onRestorePlantationSirenService={handleRestorePlantationSirenService}
                      onTogglePlantationSiren={handleTogglePlantationSiren}
                      onToggleStatus={() => handleToggleOrganizationStatus(selectedOrganization)}
                      plantationDirectory={plantationDirectory}
                      plantationPolicy={plantationPolicy}
                      plantationPolicyError={plantationPolicyError}
                      plantationPolicyForm={plantationPolicyForm}
                      plantationSirenError={plantationSirenError}
                      plantationInstallerPacket={plantationInstallerPacket}
                      plantationSirenNotice={plantationSirenNotice}
                      plantationSirenForm={plantationSirenForm}
                      plantationSirens={plantationSirens}
                      selectedPlantationId={selectedPlantationId}
                      selectedPlantationName={selectedPlantation?.plantationName ?? null}
                      onSelectedPlantationChange={setSelectedPlantationId}
                      isPlantationPolicyLoading={isPlantationPolicyLoading}
                      isPlantationPolicySubmitting={isPlantationPolicySubmitting}
                      isPlantationSirensLoading={isPlantationSirensLoading}
                      isPlantationSirensSubmitting={isPlantationSirensSubmitting}
                      onRefreshPlantationSirens={() =>
                        void reloadPlantationSirens({ announceCheckIn: true })
                      }
                      organization={selectedOrganization}
                    />
                  ) : (
                    <OperatorEmptyState
                      title="No organization selected."
                      message="Pick an organization card to inspect its readiness, contacts, authorities, and location posture."
                    />
                  )}
                </OperatorPanel>
              </div>
            ) : (
              <OperatorPanel
                title="Portal-only section"
                description="Organization management is only available to portal administrators."
              >
                <OperatorStatusMessage tone="warning">
                  This session keeps the scoped admin overview, but it cannot manage the
                  full organization directory.
                </OperatorStatusMessage>
              </OperatorPanel>
            )
          ) : null}
        </>
      ) : null}
    </>
  )
}

function AdminWorkspaceLoadingState() {
  return (
    <>
      <OperatorPanel
        title="Workspace sections"
        description="Switch between the overview, card visibility, and organization management sections."
      >
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-10 w-28 rounded-full" />
          <Skeleton className="h-10 w-24 rounded-full" />
          <Skeleton className="h-10 w-32 rounded-full" />
          <Skeleton className="h-10 w-28 rounded-full" />
          <Skeleton className="h-10 w-32 rounded-full" />
        </div>
      </OperatorPanel>

      <OperatorPanel
        title="Readiness posture"
        description="Review organization readiness, contacts, and launch status."
      >
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`admin-loading-metric-${index}`}
              className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
            >
              <Skeleton className="h-4 w-28" />
              <Skeleton className="mt-3 h-8 w-20" />
              <Skeleton className="mt-3 h-4 w-full" />
            </div>
          ))}
        </div>
      </OperatorPanel>

      <OperatorPanel
        title="Recent stock activity"
        description="Review the latest B2C stock batches visible in the current admin scope."
      >
        <div className="grid gap-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`admin-loading-stock-${index}`}
              className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </div>
      </OperatorPanel>
    </>
  )
}

function PortalOrganizationSummary({
  organizations,
}: {
  organizations: OperatorOrganizationRecord[]
}) {
  const spotlight = organizations
    .slice()
    .sort((left, right) => {
      const leftScore =
        (left.fleetEnabled ? 2 : 0)
        + (["active", "live", "ready"].includes(normalizeToken(left.pilotStatus))
          ? 1
          : 0)
      const rightScore =
        (right.fleetEnabled ? 2 : 0)
        + (["active", "live", "ready"].includes(normalizeToken(right.pilotStatus))
          ? 1
          : 0)

      return rightScore - leftScore || left.name.localeCompare(right.name)
    })
    .slice(0, 6)

  return (
    <div className="grid gap-3">
      {spotlight.map((organization) => (
        <div
          key={organization.id}
          className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold">{organization.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {[organization.city, organization.region].filter(Boolean).join(" · ") || "Location not set"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-secondary px-3 py-1 text-secondary-foreground">
                {organization.fleetEnabled ? "Fleet enabled" : "Fleet off"}
              </span>
              <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                {formatPilotStatus(organization.pilotStatus)}
              </span>
            </div>
          </div>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            {(buildNotificationChannelList(organization).join(" · ") || "No alert channels")}
            {" · "}
            {organization.fleetSize ? `${organization.fleetSize} planned assets` : "Fleet size not set"}
          </p>
        </div>
      ))}
    </div>
  )
}

function ScopedOrganizationSummary({
  fleetRegistrations,
  organization,
}: {
  fleetRegistrations: PublicLookupData[]
  organization: OperatorOrganizationDetail
}) {
  const notificationChannels = buildNotificationChannelList(organization)
  const activatedCount = fleetRegistrations.filter(
    (record) => Boolean(record.assignment?.activatedAt)
  ).length
  const locationLabel =
    [organization.city, organization.region].filter(Boolean).join(" · ") ||
    "Location not set"

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4 md:col-span-2 xl:col-span-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {titleizeToken(organization.type)}
            </p>
            <h3 className="mt-2 text-2xl font-semibold">{organization.name}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{locationLabel}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
              {organization.isActive ? "Active" : "Inactive"}
            </span>
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              {formatPilotStatus(organization.pilotStatus)}
            </span>
          </div>
        </div>
      </div>
      <ReadinessCard
        label="Pilot status"
        note={
          organization.pilotStartedAt
            ? `Started ${formatDateTime(organization.pilotStartedAt)}`
            : "Pilot start date not recorded."
        }
        tone={getPilotTone(organization.pilotStatus)}
        value={formatPilotStatus(organization.pilotStatus)}
      />
      <ReadinessCard
        label="Fleet enablement"
        note={
          organization.fleetEnabled
            ? `${organization.fleetCategory ? titleizeToken(organization.fleetCategory) : "Fleet category unset"} · ${fleetRegistrations.length} fleet tags`
            : "Fleet fields remain available but are not enabled for this organization."
        }
        tone={organization.fleetEnabled ? "success" : "warning"}
        value={organization.fleetEnabled ? "Enabled" : "Not enabled"}
      />
      <ReadinessCard
        label="Notification channels"
        note={
          notificationChannels.length
            ? notificationChannels.join(" · ")
            : "Email, SMS, and WhatsApp fallbacks are not configured."
        }
        tone={
          notificationChannels.length >= 2
            ? "success"
            : notificationChannels.length === 1
              ? "warning"
              : "danger"
        }
        value={
          notificationChannels.length
            ? `${notificationChannels.length} configured`
            : "Not configured"
        }
      />
      <ReadinessCard
        label="Fleet onboarding"
        note={`${activatedCount} activated · ${fleetRegistrations.length} fleet registrations`}
        tone={activatedCount > 0 ? "success" : fleetRegistrations.length ? "warning" : "neutral"}
        value={`${fleetRegistrations.length} tags`}
      />
      <ReadinessCard
        label="Coverage"
        note={`${organization._count?.incidents || 0} incidents · ${organization._count?.authorities || 0} authorities`}
        tone={organization._count?.incidents ? "warning" : "neutral"}
        value={`${organization._count?.locations || 0} locations · ${organization._count?.nfcCards || 0} cards`}
      />
    </div>
  )
}

function ReadinessCard({
  label,
  note,
  tone,
  value,
}: {
  label: string
  note: string
  tone: "danger" | "neutral" | "success" | "warning"
  value: string
}) {
  const toneClass =
    tone === "success"
      ? "border-chart-3/40 bg-chart-3/8"
      : tone === "warning"
        ? "border-chart-4/40 bg-chart-4/8"
        : tone === "danger"
          ? "border-destructive/35 bg-destructive/8"
          : "border-border/70 bg-background/65"

  return (
    <div className={`rounded-3xl border px-4 py-4 ${toneClass}`}>
      <div className="text-sm font-medium text-muted-foreground">{label}</div>
      <div className="mt-2 text-xl font-semibold">{value}</div>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{note}</p>
    </div>
  )
}

function PortalOrganizationDetail({
  isPlantationSirensLoading,
  isPlantationSirensSubmitting,
  isPlantationPolicyLoading,
  isPlantationPolicySubmitting,
  isSubmitting,
  notificationChannelForm,
  onNotificationChannelChange,
  onPlantationSirenFormChange,
  onPlantationPolicyChange,
  onRotatePlantationSirenHeartbeatKey,
  onSchedulePlantationSirenMaintenance,
  onAddPlantationSirenMaintenanceNote,
  onCreatePlantationSiren,
  onDeletePlantationSiren,
  onPlantationSirenHeartbeat,
  onPlantationSirenMaintenance,
  onRefreshPlantationSirens,
  onResetNotificationChannels,
  onResetPlantationPolicy,
  onRestorePlantationSirenService,
  onSaveNotificationChannels,
  onSavePlantationPolicy,
  onSelectedPlantationChange,
  onTogglePlantationSiren,
  onToggleStatus,
  organization,
  plantationDirectory,
  plantationPolicy,
  plantationPolicyError,
  plantationPolicyForm,
  plantationSirenError,
  plantationInstallerPacket,
  plantationSirenNotice,
  plantationSirenForm,
  plantationSirens,
  selectedPlantationId,
  selectedPlantationName,
}: {
  isPlantationSirensLoading: boolean
  isPlantationSirensSubmitting: boolean
  isPlantationPolicyLoading: boolean
  isPlantationPolicySubmitting: boolean
  isSubmitting: boolean
  notificationChannelForm: NotificationChannelFormState
  onNotificationChannelChange: (
    key: keyof NotificationChannelFormState,
    value: string
  ) => void
  onPlantationSirenFormChange: (
    key: keyof PlantationSirenFormState,
    value: boolean | string
  ) => void
  onPlantationPolicyChange: (
    key: keyof PlantationPolicyFormState,
    value: boolean | string
  ) => void
  onCreatePlantationSiren: (event: FormEvent<HTMLFormElement>) => void
  onRotatePlantationSirenHeartbeatKey: (siren: PlantationSirenRecord) => void
  onSchedulePlantationSirenMaintenance: (
    siren: PlantationSirenRecord,
    schedule: { note?: string | null; scheduledEndAt: string; scheduledStartAt: string }
  ) => void
  onAddPlantationSirenMaintenanceNote: (
    siren: PlantationSirenRecord,
    note: string
  ) => void
  onDeletePlantationSiren: (siren: PlantationSirenRecord) => void
  onPlantationSirenHeartbeat: (siren: PlantationSirenRecord) => void
  onPlantationSirenMaintenance: (
    siren: PlantationSirenRecord,
    enabled: boolean
  ) => void
  onRefreshPlantationSirens: () => void
  onResetNotificationChannels: () => void
  onResetPlantationPolicy: () => void
  onRestorePlantationSirenService: (siren: PlantationSirenRecord) => void
  onSaveNotificationChannels: (event: FormEvent<HTMLFormElement>) => void
  onSavePlantationPolicy: (event: FormEvent<HTMLFormElement>) => void
  onSelectedPlantationChange: (value: string) => void
  onTogglePlantationSiren: (siren: PlantationSirenRecord) => void
  onToggleStatus: () => void
  organization: OperatorOrganizationDetail
  plantationDirectory: PlantationDirectoryEntry[]
  plantationPolicy: PlantationPolicyRecord | null
  plantationPolicyError: string | null
  plantationPolicyForm: PlantationPolicyFormState
  plantationSirenError: string | null
  plantationInstallerPacket: PlantationSirenInstallerPacket | null
  plantationSirenNotice: string | null
  plantationSirenForm: PlantationSirenFormState
  plantationSirens: PlantationSirenRecord[]
  selectedPlantationId: string
  selectedPlantationName: string | null
}) {
  const primaryOwner = organization.users?.find((user) => user.isPrimaryOwner) || null

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            {titleizeToken(organization.type)}
          </p>
          <h3 className="mt-2 text-2xl font-semibold">{organization.name}</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {[organization.city, organization.region].filter(Boolean).join(" · ") || "Location not set"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
            {organization.isActive ? "Active" : "Inactive"}
          </span>
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {formatPilotStatus(organization.pilotStatus)}
          </span>
        </div>
      </div>

      <ScopedOrganizationSummary
        fleetRegistrations={[]}
        organization={organization}
      />

      <div className="grid gap-3 md:grid-cols-2">
        <DetailList
          items={[
            `Users ${organization._count?.users || 0}`,
            `Authorities ${organization._count?.authorities || 0}`,
            `Incidents ${organization._count?.incidents || 0}`,
            `Fleet tags ${organization._count?.fleetTags || 0}`,
          ]}
          title="Counts"
        />
        <DetailList
          items={[
            organization.contactEmail || "No contact email",
            organization.contactPhone || "No contact phone",
            buildNotificationChannelList(organization).join(" · ") || "No alert channels",
            organization.fleetAdminEmail || "No fleet admin email",
          ]}
          title="Contacts and alerts"
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <DetailList
          items={[
            organization.primaryContactName || "No primary contact",
            organization.billingEmail || "No billing email",
            organization.customerCategory
              ? titleizeToken(organization.customerCategory)
              : "No customer category",
            `Onboarding ${titleizeToken(organization.onboardingStatus || "draft")}`,
          ]}
          title="Commercial profile"
        />
        <DetailList
          items={[
            primaryOwner?.name || "No orchard admin provisioned",
            primaryOwner?.email || "No orchard admin email",
            primaryOwner?.phoneNumber || "No orchard admin phone",
            [
              primaryOwner?.inviteStatus
                ? `Invite ${titleizeToken(primaryOwner.inviteStatus)}`
                : null,
              primaryOwner?.accountStatus
                ? `Account ${titleizeToken(primaryOwner.accountStatus)}`
                : null,
            ].filter(Boolean).join(" · ") || "Invite not started",
          ]}
          title="Orchard admin access"
        />
      </div>

      {organization.orchardOnboardingProfile ? (
        <DetailList
          items={[
            organization.orchardOnboardingProfile.primaryCropType
              ? `Crop ${organization.orchardOnboardingProfile.primaryCropType}`
              : "No crop baseline",
            organization.orchardOnboardingProfile.primaryVariety
              ? `Variety ${organization.orchardOnboardingProfile.primaryVariety}`
              : "No variety baseline",
            organization.orchardOnboardingProfile.implementationMode
              ? `Mode ${titleizeToken(organization.orchardOnboardingProfile.implementationMode)}`
              : "No implementation mode",
            organization.orchardOnboardingProfile.approximateTreeCount !== null
            && organization.orchardOnboardingProfile.approximateTreeCount !== undefined
              ? `Approx trees ${organization.orchardOnboardingProfile.approximateTreeCount}`
              : "No tree estimate",
            organization.orchardOnboardingProfile.qrRolloutRequired
              ? "QR rollout required"
              : "QR rollout not required",
          ]}
          title="Orchard onboarding baseline"
        />
      ) : null}

      <DetailList
        items={
          organization.locations?.length
            ? organization.locations.map((location) =>
                [location.name, location.address].filter(Boolean).join(" · ")
              )
            : ["No locations linked"]
        }
        title="Locations"
      />

      <DetailList
        items={
          organization.authorities?.length
            ? organization.authorities.map((authority) =>
                [authority.fullName || authority.email || authority.id, authority.position]
                  .filter(Boolean)
                  .join(" · ")
              )
            : ["No authorities linked"]
        }
        title="Authorities"
      />

      <form className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4" onSubmit={onSaveNotificationChannels}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Notification channels</p>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              Update the organization-level recipients used when authority notifications fall back to org coverage.
            </p>
          </div>
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
            {buildNotificationChannelList({
              notificationEmail: notificationChannelForm.notificationEmail,
              notificationPhone: notificationChannelForm.notificationPhone,
              notificationWhatsapp: notificationChannelForm.notificationWhatsapp,
            }).join(" · ") || "No alert channels"}
          </span>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <label className="grid gap-2 text-sm">
            <span className="font-medium">Notification email</span>
            <input
              aria-label="Edit notification email"
              className={INPUT_CLASS}
              type="email"
              value={notificationChannelForm.notificationEmail}
              onChange={(event) =>
                onNotificationChannelChange("notificationEmail", event.target.value)
              }
            />
          </label>
          <label className="grid gap-2 text-sm">
            <span className="font-medium">Notification phone</span>
            <input
              aria-label="Edit notification phone"
              className={INPUT_CLASS}
              value={notificationChannelForm.notificationPhone}
              onChange={(event) =>
                onNotificationChannelChange("notificationPhone", event.target.value)
              }
            />
          </label>
          <label className="grid gap-2 text-sm">
            <span className="font-medium">Notification WhatsApp</span>
            <input
              aria-label="Edit notification WhatsApp"
              className={INPUT_CLASS}
              value={notificationChannelForm.notificationWhatsapp}
              onChange={(event) =>
                onNotificationChannelChange("notificationWhatsapp", event.target.value)
              }
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <Button disabled={isSubmitting} type="submit">
            {isSubmitting ? "Saving…" : "Save notification channels"}
          </Button>
          <Button
            disabled={isSubmitting}
            onClick={onResetNotificationChannels}
            type="button"
            variant="outline"
          >
            Reset
          </Button>
        </div>
      </form>

      <PlantationPolicyEditor
        isLoading={isPlantationPolicyLoading}
        isSubmitting={isPlantationPolicySubmitting}
        onChange={onPlantationPolicyChange}
        onReset={onResetPlantationPolicy}
        onSave={onSavePlantationPolicy}
        onSelectedPlantationChange={onSelectedPlantationChange}
        plantationDirectory={plantationDirectory}
        plantationPolicy={plantationPolicy}
        plantationPolicyError={plantationPolicyError}
        plantationPolicyForm={plantationPolicyForm}
        plantationSirenError={plantationSirenError}
        plantationInstallerPacket={plantationInstallerPacket}
        plantationSirenNotice={plantationSirenNotice}
        plantationSirenForm={plantationSirenForm}
        plantationSirens={plantationSirens}
        selectedPlantationId={selectedPlantationId}
        selectedPlantationName={selectedPlantationName}
        onCreateSiren={onCreatePlantationSiren}
        onRotateSirenHeartbeatKey={onRotatePlantationSirenHeartbeatKey}
        onScheduleSirenMaintenance={onSchedulePlantationSirenMaintenance}
        onAddSirenMaintenanceNote={onAddPlantationSirenMaintenanceNote}
        onDeleteSiren={onDeletePlantationSiren}
        onHeartbeatSiren={onPlantationSirenHeartbeat}
        onRefreshSirens={onRefreshPlantationSirens}
        onSirenFormChange={onPlantationSirenFormChange}
        onMaintenanceSiren={onPlantationSirenMaintenance}
        onRestoreSiren={onRestorePlantationSirenService}
        onToggleSiren={onTogglePlantationSiren}
        isSirenLoading={isPlantationSirensLoading}
        isSirenSubmitting={isPlantationSirensSubmitting}
      />

      <div className="flex flex-wrap gap-3">
        <Button disabled={isSubmitting} onClick={onToggleStatus} variant="outline">
          {organization.isActive ? "Deactivate organization" : "Activate organization"}
        </Button>
      </div>
    </div>
  )
}

function PlantationPolicyEditor({
  isLoading,
  isSirenLoading,
  isSirenSubmitting,
  isSubmitting,
  onAddSirenMaintenanceNote,
  onChange,
  onCreateSiren,
  onDeleteSiren,
  onHeartbeatSiren,
  onMaintenanceSiren,
  onRefreshSirens,
  onReset,
  onRotateSirenHeartbeatKey,
  onRestoreSiren,
  onSave,
  onScheduleSirenMaintenance,
  onSelectedPlantationChange,
  onSirenFormChange,
  onToggleSiren,
  plantationDirectory,
  plantationPolicy,
  plantationPolicyError,
  plantationPolicyForm,
  plantationSirenError,
  plantationInstallerPacket,
  plantationSirenNotice,
  plantationSirenForm,
  plantationSirens,
  selectedPlantationId,
  selectedPlantationName,
}: {
  isLoading: boolean
  isSirenLoading: boolean
  isSirenSubmitting: boolean
  isSubmitting: boolean
  onAddSirenMaintenanceNote: (siren: PlantationSirenRecord, note: string) => void
  onChange: (
    key: keyof PlantationPolicyFormState,
    value: boolean | string
  ) => void
  onCreateSiren: (event: FormEvent<HTMLFormElement>) => void
  onDeleteSiren: (siren: PlantationSirenRecord) => void
  onHeartbeatSiren: (siren: PlantationSirenRecord) => void
  onMaintenanceSiren: (siren: PlantationSirenRecord, enabled: boolean) => void
  onRefreshSirens: () => void
  onReset: () => void
  onRotateSirenHeartbeatKey: (siren: PlantationSirenRecord) => void
  onRestoreSiren: (siren: PlantationSirenRecord) => void
  onSave: (event: FormEvent<HTMLFormElement>) => void
  onScheduleSirenMaintenance: (
    siren: PlantationSirenRecord,
    schedule: { note?: string | null; scheduledEndAt: string; scheduledStartAt: string }
  ) => void
  onSelectedPlantationChange: (value: string) => void
  onSirenFormChange: (
    key: keyof PlantationSirenFormState,
    value: boolean | string
  ) => void
  onToggleSiren: (siren: PlantationSirenRecord) => void
  plantationDirectory: PlantationDirectoryEntry[]
  plantationPolicy: PlantationPolicyRecord | null
  plantationPolicyError: string | null
  plantationPolicyForm: PlantationPolicyFormState
  plantationSirenError: string | null
  plantationInstallerPacket: PlantationSirenInstallerPacket | null
  plantationSirenNotice: string | null
  plantationSirenForm: PlantationSirenFormState
  plantationSirens: PlantationSirenRecord[]
  selectedPlantationId: string
  selectedPlantationName: string | null
}) {
  const selectedDirectoryEntry =
    plantationDirectory.find((entry) => entry.id === selectedPlantationId) ?? null
  const selectedLanguageLabel = plantationPolicyForm.defaultLocalLanguageCode
    ? getPlantationLocalLanguageOption(plantationPolicyForm.defaultLocalLanguageCode)?.label
    : null
  const [maintenanceDrafts, setMaintenanceDrafts] = useState<
    Record<
      string,
      {
        note: string
        scheduledEndAt: string
        scheduledStartAt: string
      }
    >
  >({})
  const [installerPacketError, setInstallerPacketError] = useState<string | null>(null)
  const [isPrintingInstallerPacket, setIsPrintingInstallerPacket] = useState(false)

  function getMaintenanceDraft(siren: PlantationSirenRecord) {
    return (
      maintenanceDrafts[siren.id] ?? {
        note: siren.scheduledMaintenanceNote ?? "",
        scheduledEndAt: formatDateTimeLocalInputValue(siren.scheduledMaintenanceEndAt),
        scheduledStartAt: formatDateTimeLocalInputValue(siren.scheduledMaintenanceStartAt),
      }
    )
  }

  function updateMaintenanceDraft(
    sirenId: string,
    key: "note" | "scheduledEndAt" | "scheduledStartAt",
    value: string
  ) {
    setMaintenanceDrafts((current) => ({
      ...current,
      [sirenId]: {
        ...(current[sirenId] ?? {
          note: "",
          scheduledEndAt: "",
          scheduledStartAt: "",
        }),
        [key]: value,
      },
    }))
  }

  const installerPacketSiren =
    plantationInstallerPacket
      ? plantationSirens.find((item) => item.id === plantationInstallerPacket.sirenId) ?? null
      : null
  const installerEndpointUrl = plantationInstallerPacket
    ? typeof window === "undefined"
      ? plantationInstallerPacket.endpointPath
      : new URL(plantationInstallerPacket.endpointPath, window.location.origin).toString()
    : null
  const installerCurlCommand =
    plantationInstallerPacket && installerEndpointUrl
      ? [
          `curl -X POST "${installerEndpointUrl}" \\`,
          `  -H "Content-Type: application/json" \\`,
          `  -H "${plantationInstallerPacket.headerName}: ${plantationInstallerPacket.apiKey}" \\`,
          `  -d '{"sirenId":"${plantationInstallerPacket.sirenId}","sourceLabel":"solar_gsm_unit","batteryPercent":85,"signalStrength":"good"}'`,
        ].join("\n")
      : null
  const installerQrPayload =
    plantationInstallerPacket && installerEndpointUrl
      ? buildInstallerPacketQrPayload(plantationInstallerPacket, installerEndpointUrl)
      : null
  const installerOnboardingHistory = (
    installerPacketSiren?.maintenanceHistory?.filter((event) =>
      [
        "installer_packet_generated",
        "installer_packet_regenerated",
        "first_device_check_in_confirmed",
      ].includes(String(event.eventType || ""))
    ) ?? []
  ).slice(0, 4)

  async function handlePrintInstallerSheet() {
    if (!plantationInstallerPacket || !installerEndpointUrl || !installerQrPayload) {
      return
    }

    setIsPrintingInstallerPacket(true)
    setInstallerPacketError(null)

    try {
      const qrDataUrl = await renderQrCodeDataUrl(installerQrPayload)
      const printWindow = window.open("", "_blank", "noopener,noreferrer,width=980,height=760")

      if (!printWindow) {
        throw new Error("Unable to open the installer handoff sheet")
      }

      printWindow.document.write(
        buildInstallerPacketPrintDocument({
          displayName: plantationInstallerPacket.displayName,
          endpointUrl: installerEndpointUrl,
          firstDeviceCheckInAt: installerPacketSiren?.firstDeviceCheckInAt || null,
          headerLine: `${plantationInstallerPacket.headerName}: ${plantationInstallerPacket.apiKey}`,
          onboardingHistory: installerOnboardingHistory,
          plantationLabel: selectedPlantationName || selectedPlantationId || "Plantation",
          provisioningLabel: formatProvisioningStatusLabel(
            installerPacketSiren?.provisioningStatus || "awaiting_first_checkin"
          ),
          qrDataUrl,
          qrPayload: installerQrPayload,
          zoneLabel: installerPacketSiren?.zoneName || "Central coverage",
        })
      )
      printWindow.document.close()
      printWindow.focus()
    } catch (error) {
      setInstallerPacketError(
        error instanceof Error
          ? error.message
          : "Unable to print the installer handoff sheet."
      )
    } finally {
      setIsPrintingInstallerPacket(false)
    }
  }

  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Plantation policy controls</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Configure the panic, reporting, language, and siren defaults that apply
            across one plantation deployment.
          </p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
          {plantationPolicy?.configStatus === "configured" ? "Configured" : "Default rules"}
        </span>
      </div>

      {plantationPolicyError ? (
        <div className="mt-4">
          <OperatorStatusMessage tone="danger">
            {plantationPolicyError}
          </OperatorStatusMessage>
        </div>
      ) : null}

      {plantationSirenError ? (
        <div className="mt-4">
          <OperatorStatusMessage tone="danger">
            {plantationSirenError}
          </OperatorStatusMessage>
        </div>
      ) : null}

      {plantationSirenNotice ? (
        <div className="mt-4">
          <OperatorStatusMessage>{plantationSirenNotice}</OperatorStatusMessage>
        </div>
      ) : null}

      {plantationInstallerPacket ? (
        <div className="mt-4 rounded-3xl border border-primary/20 bg-primary/5 px-4 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Installer heartbeat packet</p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Share this one-time setup packet with the installer, then refresh the
                siren registry after the device sends its first heartbeat.
              </p>
            </div>
            <span
              className={resolveProvisioningStatusTone(
                installerPacketSiren?.provisioningStatus || "awaiting_first_checkin"
              )}
            >
              {formatProvisioningStatusLabel(
                installerPacketSiren?.provisioningStatus || "awaiting_first_checkin"
              )}
            </span>
          </div>

          {installerPacketSiren?.firstDeviceCheckInAt ? (
            <div className="mt-4">
              <OperatorStatusMessage tone="info">
                First device heartbeat confirmed for {installerPacketSiren.displayName}.{" "}
                Check-in received {formatDateTime(installerPacketSiren.firstDeviceCheckInAt)}.
              </OperatorStatusMessage>
            </div>
          ) : null}

          {installerPacketError ? (
            <div className="mt-4">
              <OperatorStatusMessage tone="danger">
                {installerPacketError}
              </OperatorStatusMessage>
            </div>
          ) : null}

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-border/70 bg-background/80 px-3 py-3 text-sm">
              <p className="font-medium text-foreground">
                {plantationInstallerPacket.displayName}
              </p>
              <p className="mt-1 text-muted-foreground">
                Siren ID {plantationInstallerPacket.sirenId}
              </p>
              <p className="mt-1 text-muted-foreground">
                Generated {formatDateTime(plantationInstallerPacket.generatedAt)}
              </p>
              {installerPacketSiren?.firstDeviceCheckInAt ? (
                <p className="mt-1 text-foreground">
                  First device check-in {formatDateTime(installerPacketSiren.firstDeviceCheckInAt)}
                </p>
              ) : (
                <p className="mt-1 text-primary">
                  Waiting for the first device heartbeat.
                </p>
              )}
            </div>
            <div className="rounded-2xl border border-border/70 bg-background/80 px-3 py-3 text-sm">
              <p className="font-medium text-foreground">Installer checklist</p>
              <p className="mt-1 text-muted-foreground">
                1. Program the siren with the Siren ID and heartbeat key.
              </p>
              <p className="mt-1 text-muted-foreground">
                2. Send a heartbeat to the endpoint below with the header name shown.
              </p>
              <p className="mt-1 text-muted-foreground">
                3. Use Refresh sirens to confirm the first check-in in admin.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              disabled={!installerQrPayload || isPrintingInstallerPacket}
              type="button"
              variant="outline"
              onClick={() => {
                void handlePrintInstallerSheet()
              }}
            >
              {isPrintingInstallerPacket ? "Preparing installer sheet…" : "Print installer sheet"}
            </Button>
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.8fr)]">
            <div className="rounded-2xl border border-border/70 bg-background/80 px-3 py-3 text-sm">
              <p className="font-medium text-foreground">Heartbeat endpoint</p>
              <p className="mt-2 break-all text-muted-foreground">
                {installerEndpointUrl || plantationInstallerPacket.endpointPath}
              </p>
              <p className="mt-3 font-medium text-foreground">Heartbeat header</p>
              <p className="mt-2 break-all text-muted-foreground">
                {plantationInstallerPacket.headerName}: {plantationInstallerPacket.apiKey}
              </p>
              <p className="mt-3 font-medium text-foreground">Provisioning QR payload</p>
              <p className="mt-2 break-all text-xs leading-6 text-muted-foreground">
                {installerQrPayload || "QR payload is unavailable."}
              </p>
            </div>
            {installerQrPayload ? <InstallerPacketQrCode payload={installerQrPayload} /> : null}
          </div>
          <div className="mt-4 grid gap-3">
            {installerCurlCommand ? (
              <div className="rounded-2xl border border-border/70 bg-background/80 px-3 py-3 text-sm">
                <p className="font-medium text-foreground">Sample installer command</p>
                <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-xs leading-6 text-muted-foreground">
                  {installerCurlCommand}
                </pre>
              </div>
            ) : null}
            {installerOnboardingHistory.length ? (
              <div className="rounded-2xl border border-border/70 bg-background/80 px-3 py-3 text-sm">
                <p className="font-medium text-foreground">Onboarding history</p>
                <div className="mt-3 grid gap-2">
                  {installerOnboardingHistory.map((event) => (
                    <div
                      key={event.id}
                      className="rounded-2xl bg-background/75 px-3 py-2 text-sm text-muted-foreground"
                    >
                      <span className="font-medium text-foreground">
                        {titleizeToken(event.eventType || "event")}
                      </span>
                      <span>{` · ${formatDateTime(event.createdAt)}`}</span>
                      {event.note ? <p className="mt-1">{event.note}</p> : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {!plantationDirectory.length ? (
        <div className="mt-4">
          <OperatorEmptyState
            title="No plantation activations in scope."
            message="Activate at least one plantations kit in this organization to load editable policy controls."
          />
        </div>
      ) : (
        <>
          <form className="grid gap-4" onSubmit={onSave}>
            <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Plantation</span>
                <select
                  aria-label="Selected plantation"
                  className={INPUT_CLASS}
                  value={selectedPlantationId}
                  onChange={(event) => onSelectedPlantationChange(event.target.value)}
                >
                  {plantationDirectory.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.plantationName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Plantation name</span>
                <input
                  aria-label="Plantation name"
                  className={INPUT_CLASS}
                  value={plantationPolicyForm.plantationName}
                  onChange={(event) => onChange("plantationName", event.target.value)}
                />
              </label>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <DetailList
                items={[
                  selectedPlantationName || "Unnamed plantation",
                  `${selectedDirectoryEntry?.tagCount || 0} activated tags in scope`,
                ]}
                title="Coverage"
              />
              <DetailList
                items={[
                  `${selectedDirectoryEntry?.zoneCount || 0} named zones`,
                  selectedDirectoryEntry?.zones.join(" · ") || "No zones named yet",
                ]}
                title="Zone footprint"
              />
              <DetailList
                items={[
                  selectedLanguageLabel || "English only",
                  plantationPolicyForm.dualLanguageEnabled
                    ? "Dual language enabled"
                    : "Dual language disabled",
                ]}
                title="Language output"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Local language</span>
                <select
                  aria-label="Plantation local language"
                  className={INPUT_CLASS}
                  value={plantationPolicyForm.defaultLocalLanguageCode}
                  onChange={(event) =>
                    onChange("defaultLocalLanguageCode", event.target.value)
                  }
                >
                  <option value="">English only</option>
                  {PLANTATION_LOCAL_LANGUAGE_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Panic SMS recipients</span>
                <input
                  aria-label="Panic SMS recipient limit"
                  className={INPUT_CLASS}
                  inputMode="numeric"
                  max={5}
                  min={1}
                  value={plantationPolicyForm.panicSmsRecipientLimit}
                  onChange={(event) =>
                    onChange("panicSmsRecipientLimit", event.target.value)
                  }
                />
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                <input
                  aria-label="Enable panic SMS"
                  checked={plantationPolicyForm.panicSmsEnabled}
                  type="checkbox"
                  onChange={(event) => onChange("panicSmsEnabled", event.target.checked)}
                />
                <span>Send SMS for panic alerts</span>
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                <input
                  aria-label="Enable dual language"
                  checked={plantationPolicyForm.dualLanguageEnabled}
                  type="checkbox"
                  onChange={(event) =>
                    onChange("dualLanguageEnabled", event.target.checked)
                  }
                />
                <span>Show English + selected local language</span>
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                <input
                  aria-label="Notify emergency contacts on panic"
                  checked={plantationPolicyForm.notifyEmergencyContactsOnPanic}
                  type="checkbox"
                  onChange={(event) =>
                    onChange(
                      "notifyEmergencyContactsOnPanic",
                      event.target.checked
                    )
                  }
                />
                <span>Notify emergency contacts on panic</span>
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                <input
                  aria-label="Notify emergency contacts on report"
                  checked={plantationPolicyForm.notifyEmergencyContactsOnReport}
                  type="checkbox"
                  onChange={(event) =>
                    onChange(
                      "notifyEmergencyContactsOnReport",
                      event.target.checked
                    )
                  }
                />
                <span>Notify emergency contacts on report</span>
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                <input
                  aria-label="Notify nearest authority"
                  checked={plantationPolicyForm.notifyNearestAuthority}
                  type="checkbox"
                  onChange={(event) =>
                    onChange("notifyNearestAuthority", event.target.checked)
                  }
                />
                <span>Notify nearest authority when rules require it</span>
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                <input
                  aria-label="Trigger sirens on panic"
                  checked={plantationPolicyForm.triggerSirensOnPanic}
                  type="checkbox"
                  onChange={(event) =>
                    onChange("triggerSirensOnPanic", event.target.checked)
                  }
                />
                <span>Trigger plantation sirens on panic</span>
              </label>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Siren routing</span>
                <select
                  aria-label="Siren routing mode"
                  className={INPUT_CLASS}
                  value={plantationPolicyForm.sirenRoutingMode}
                  onChange={(event) => onChange("sirenRoutingMode", event.target.value)}
                >
                  {PLANTATION_SIREN_ROUTING_OPTIONS.map((option) => (
                    <option key={option.value || "blank"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Siren cooldown (minutes)</span>
                <input
                  aria-label="Siren cooldown minutes"
                  className={INPUT_CLASS}
                  inputMode="numeric"
                  max={120}
                  min={1}
                  value={plantationPolicyForm.sirenCooldownMinutes}
                  onChange={(event) => onChange("sirenCooldownMinutes", event.target.value)}
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Heartbeat stale window (minutes)</span>
                <input
                  aria-label="Siren heartbeat stale minutes"
                  className={INPUT_CLASS}
                  inputMode="numeric"
                  max={1440}
                  min={5}
                  value={plantationPolicyForm.sirenHeartbeatStaleMinutes}
                  onChange={(event) =>
                    onChange("sirenHeartbeatStaleMinutes", event.target.value)
                  }
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Auto-disable after consecutive failures</span>
                <input
                  aria-label="Siren failure disable threshold"
                  className={INPUT_CLASS}
                  inputMode="numeric"
                  max={10}
                  min={1}
                  value={plantationPolicyForm.sirenFailureDisableThreshold}
                  onChange={(event) =>
                    onChange("sirenFailureDisableThreshold", event.target.value)
                  }
                />
              </label>
              <div className="rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm text-muted-foreground">
                {isLoading
                  ? "Loading saved plantation defaults…"
                  : "These controls feed backend siren routing, heartbeat staleness, failure auto-disable, and multi-device escalation behavior."}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                disabled={isLoading || isSubmitting || !selectedPlantationId}
                type="submit"
              >
                {isSubmitting ? "Saving…" : "Save plantation policy"}
              </Button>
              <Button
                disabled={isLoading || isSubmitting || !selectedPlantationId}
                onClick={onReset}
                type="button"
                variant="outline"
              >
                Reset
              </Button>
            </div>
          </form>

          <div className="mt-6 rounded-3xl border border-border/70 bg-background/60 px-4 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Siren registry</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  Add the SIM-backed sirens that should receive panic-triggered SMS
                  activation commands for this plantation.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                  {plantationSirens.length} registered
                </span>
                <Button
                  disabled={isSirenLoading || isSirenSubmitting || !selectedPlantationId}
                  onClick={onRefreshSirens}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  Refresh sirens
                </Button>
              </div>
            </div>

            {plantationSirens.length ? (
              <div className="mt-4 grid gap-3">
                {plantationSirens.map((siren) => {
                  const maintenanceDraft = getMaintenanceDraft(siren)
                  const locationSummary =
                    [
                      siren.locationLabel || null,
                      siren.latitude !== null && siren.latitude !== undefined
                        ? `${siren.latitude}, ${siren.longitude ?? "?"}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Location not set"

                  return (
                    <div
                      key={siren.id}
                      className="rounded-2xl border border-border/70 bg-background/70 px-4 py-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-medium">{siren.displayName}</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {[
                              siren.zoneName || "Central coverage",
                              siren.phoneNumber || "No SIM number",
                              `Order ${siren.escalationOrder ?? 0}`,
                            ].join(" · ")}
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {[
                              `Heartbeat ${formatDateTime(siren.lastHeartbeatAt)}`,
                              `Last seen ${formatDateTime(siren.lastSeenAt)}`,
                              `Failures ${siren.consecutiveFailureCount ?? 0}`,
                            ].join(" · ")}
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {[
                              `Provisioning ${formatProvisioningStatusLabel(
                                siren.provisioningStatus
                              )}`,
                              `Provisioned ${formatDateTime(siren.heartbeatProvisionedAt)}`,
                              siren.heartbeatProvisionedByName || null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            First device check-in {formatDateTime(siren.firstDeviceCheckInAt)}
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {[
                              locationSummary,
                              `Heartbeat source ${titleizeToken(
                                siren.lastHeartbeatSource || "unknown"
                              )}`,
                            ].join(" · ")}
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Heartbeat key {siren.heartbeatKeyPreview || "Not provisioned"}
                          </p>
                          {siren.scheduledMaintenanceStartAt || siren.scheduledMaintenanceEndAt ? (
                            <p className="mt-1 text-sm text-muted-foreground">
                              Scheduled{" "}
                              {[
                                formatDateTime(siren.scheduledMaintenanceStartAt),
                                formatDateTime(siren.scheduledMaintenanceEndAt),
                              ].join(" → ")}
                              {siren.scheduledMaintenanceNote
                                ? ` · ${siren.scheduledMaintenanceNote}`
                                : ""}
                            </p>
                          ) : null}
                          {siren.failureDisabledReason ? (
                            <p className="mt-1 text-sm text-destructive">
                              {siren.failureDisabledReason}
                            </p>
                          ) : null}
                          {siren.maintenanceReason ? (
                            <p className="mt-1 text-sm text-muted-foreground">
                              {siren.maintenanceReason}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                            {siren.isActive ? "Active" : "Disabled"}
                          </span>
                          <span className={resolveSirenHealthTone(siren.healthState)}>
                            {formatSirenHealthLabel(siren.healthState)}
                          </span>
                          <span
                            className={resolveProvisioningStatusTone(siren.provisioningStatus)}
                          >
                            {formatProvisioningStatusLabel(siren.provisioningStatus)}
                          </span>
                          <Button
                            disabled={isSirenSubmitting}
                            onClick={() => onToggleSiren(siren)}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            {siren.isActive ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            disabled={isSirenSubmitting}
                            onClick={() => onMaintenanceSiren(siren, !siren.maintenanceMode)}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            {siren.maintenanceMode ? "End maintenance" : "Maintenance"}
                          </Button>
                          <Button
                            disabled={isSirenSubmitting}
                            onClick={() => onHeartbeatSiren(siren)}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            Record heartbeat
                          </Button>
                          <Button
                            disabled={isSirenSubmitting}
                            onClick={() => onRotateSirenHeartbeatKey(siren)}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            Generate installer packet
                          </Button>
                          {siren.failureDisabledAt || (siren.consecutiveFailureCount ?? 0) > 0 ? (
                            <Button
                              disabled={isSirenSubmitting}
                              onClick={() => onRestoreSiren(siren)}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              Restore service
                            </Button>
                          ) : null}
                          <Button
                            disabled={isSirenSubmitting}
                            onClick={() => onDeleteSiren(siren)}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <label className="grid gap-2 text-sm">
                          <span className="font-medium">Maintenance start</span>
                          <input
                            aria-label={`Maintenance start for ${siren.displayName}`}
                            className={INPUT_CLASS}
                            type="datetime-local"
                            value={maintenanceDraft.scheduledStartAt}
                            onChange={(event) =>
                              updateMaintenanceDraft(
                                siren.id,
                                "scheduledStartAt",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="grid gap-2 text-sm">
                          <span className="font-medium">Maintenance end</span>
                          <input
                            aria-label={`Maintenance end for ${siren.displayName}`}
                            className={INPUT_CLASS}
                            type="datetime-local"
                            value={maintenanceDraft.scheduledEndAt}
                            onChange={(event) =>
                              updateMaintenanceDraft(
                                siren.id,
                                "scheduledEndAt",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="grid gap-2 text-sm md:col-span-2">
                          <span className="font-medium">Maintenance note</span>
                          <input
                            aria-label={`Maintenance note for ${siren.displayName}`}
                            className={INPUT_CLASS}
                            value={maintenanceDraft.note}
                            onChange={(event) =>
                              updateMaintenanceDraft(siren.id, "note", event.target.value)
                            }
                          />
                        </label>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-3">
                        <Button
                          disabled={
                            isSirenSubmitting
                            || !maintenanceDraft.scheduledStartAt
                            || !maintenanceDraft.scheduledEndAt
                          }
                          onClick={() =>
                            onScheduleSirenMaintenance(siren, {
                              note: maintenanceDraft.note || null,
                              scheduledEndAt: maintenanceDraft.scheduledEndAt,
                              scheduledStartAt: maintenanceDraft.scheduledStartAt,
                            })
                          }
                          size="sm"
                          type="button"
                        >
                          Schedule maintenance
                        </Button>
                        <Button
                          disabled={isSirenSubmitting || !maintenanceDraft.note.trim()}
                          onClick={() =>
                            onAddSirenMaintenanceNote(siren, maintenanceDraft.note.trim())
                          }
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          Add note
                        </Button>
                      </div>

                      {siren.maintenanceHistory?.length ? (
                        <div className="mt-4 grid gap-2">
                          <p className="text-sm font-medium">Recent maintenance history</p>
                          {siren.maintenanceHistory.slice(0, 4).map((event) => (
                            <div
                              key={event.id}
                              className="rounded-2xl border border-border/70 bg-background/65 px-3 py-3 text-sm text-muted-foreground"
                            >
                              <p className="font-medium text-foreground">
                                {titleizeToken(event.eventType)}
                              </p>
                              <p className="mt-1">
                                {[
                                  formatDateTime(event.createdAt),
                                  event.actorName || event.actorId || "System",
                                  event.sourceType ? titleizeToken(event.sourceType) : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                              {event.note ? <p className="mt-1">{event.note}</p> : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="mt-4">
                <OperatorEmptyState
                  title="No sirens registered yet."
                  message="Add the first plantation siren below so panic alerts can target a real device."
                />
              </div>
            )}

            <form className="mt-4 grid gap-4" onSubmit={onCreateSiren}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Siren name</span>
                  <input
                    aria-label="Plantation siren name"
                    className={INPUT_CLASS}
                    value={plantationSirenForm.displayName}
                    onChange={(event) =>
                      onSirenFormChange("displayName", event.target.value)
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">SIM phone number</span>
                  <input
                    aria-label="Plantation siren phone number"
                    className={INPUT_CLASS}
                    value={plantationSirenForm.phoneNumber}
                    onChange={(event) =>
                      onSirenFormChange("phoneNumber", event.target.value)
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Zone name</span>
                  <input
                    aria-label="Plantation siren zone name"
                    className={INPUT_CLASS}
                    value={plantationSirenForm.zoneName}
                    onChange={(event) =>
                      onSirenFormChange("zoneName", event.target.value)
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Escalation order</span>
                  <input
                    aria-label="Plantation siren escalation order"
                    className={INPUT_CLASS}
                    inputMode="numeric"
                    value={plantationSirenForm.escalationOrder}
                    onChange={(event) =>
                      onSirenFormChange("escalationOrder", event.target.value)
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Location label</span>
                  <input
                    aria-label="Plantation siren location label"
                    className={INPUT_CLASS}
                    value={plantationSirenForm.locationLabel}
                    onChange={(event) =>
                      onSirenFormChange("locationLabel", event.target.value)
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Latitude</span>
                  <input
                    aria-label="Plantation siren latitude"
                    className={INPUT_CLASS}
                    inputMode="decimal"
                    value={plantationSirenForm.latitude}
                    onChange={(event) =>
                      onSirenFormChange("latitude", event.target.value)
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Longitude</span>
                  <input
                    aria-label="Plantation siren longitude"
                    className={INPUT_CLASS}
                    inputMode="decimal"
                    value={plantationSirenForm.longitude}
                    onChange={(event) =>
                      onSirenFormChange("longitude", event.target.value)
                    }
                  />
                </label>
              </div>

              <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm">
                <input
                  aria-label="Plantation siren trigger on panic"
                  checked={plantationSirenForm.triggerOnPanic}
                  type="checkbox"
                  onChange={(event) =>
                    onSirenFormChange("triggerOnPanic", event.target.checked)
                  }
                />
                <span>Trigger this siren on panic</span>
              </label>

              <div className="flex flex-wrap gap-3">
                <Button
                  disabled={isSirenLoading || isSirenSubmitting || !selectedPlantationId}
                  type="submit"
                >
                  {isSirenSubmitting ? "Saving siren…" : "Add siren"}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  )
}

function DetailList({
  items,
  title,
}: {
  items: string[]
  title: string
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <p className="text-sm font-medium">{title}</p>
      <div className="mt-3 grid gap-2 text-sm leading-6 text-muted-foreground">
        {items.map((item) => (
          <div key={item} className="rounded-2xl bg-background/70 px-3 py-2">
            {item}
          </div>
        ))}
      </div>
    </div>
  )
}
